/* contentSchemas.js — per-page "structure" schema.
 *
 * Each page has an ordered `sections` list mirroring the actual
 * components the public page renders (top-to-bottom). The admin
 * edit view renders this list on the LEFT as a navigator, and the
 * currently-selected section's form on the RIGHT.
 *
 * A section has:
 *   - id          : unique key (stable across edits, scoped to the page)
 *   - label       : display name in the admin sidebar
 *   - description : short line shown under the label / above the form
 *   - fields      : optional — list of editable field descriptors.
 *                   If omitted, the section shows a "No editable
 *                   fields yet" placeholder (will be opened up later).
 *
 * Field descriptor:
 *   - path   : dot-path under `data.<sectionId>` (or anywhere — we
 *              keep the legacy top-level paths for `hero.*` to avoid
 *              breaking already-edited content).
 *   - type   : "text" | "textarea" | "url" | "image" | "list"
 *   - label  : input label
 *   - hint   : optional helper text below the input
 *   - rows   : textarea rows (optional)
 *   - item   : sub-schema for "list" type
 */

// --------------------------- HOME ---------------------------
export const HOME_SCHEMA = {
  key: "home",
  label: "Home",
  route: "/",
  sections: [
    {
      id: "hero",
      label: "Hero",
      description: "Top-of-page headline, subtitle and primary/secondary CTAs.",
      fields: [
        { path: "hero.eyebrow", type: "text", label: "Eyebrow" },
        { path: "hero.title_line_1", type: "text", label: "Title — line 1" },
        { path: "hero.title_emphasis", type: "text", label: "Title — italic emphasis word",
          hint: "Rendered italic in muted gray." },
        { path: "hero.title_line_2", type: "text", label: "Title — line 2 (underlined)" },
        { path: "hero.subtitle", type: "textarea", label: "Sub-headline", rows: 3 },
        { path: "hero.cta_primary_label", type: "text", label: "Primary CTA label" },
        { path: "hero.cta_primary_href", type: "url", label: "Primary CTA URL" },
        { path: "hero.cta_secondary_label", type: "text", label: "Secondary CTA label" },
        { path: "hero.cta_secondary_href", type: "url", label: "Secondary CTA URL" },
      ],
    },
    {
      id: "logo_strip",
      label: "Logo strip",
      description:
        "Two rows of client/partner logos shown under the hero. Each row auto-scrolls (marquee). Any logo dimensions are fine — they'll be fitted to a uniform row height with transparent spacing. Images are auto-compressed on upload.",
      fields: [
        { path: "logo_strip.style", type: "select", label: "Display style",
          hint: "Applied to every logo in the strip.",
          options: [
            { value: "color", label: "Full colour (original)" },
            { value: "white", label: "White silhouette" },
            { value: "grayscale", label: "Grayscale" },
          ],
        },
        { path: "logo_strip.line_1", type: "logo_list", label: "Line 1" },
        { path: "logo_strip.line_1_speed", type: "number", label: "Line 1 — scroll speed",
          min: 10, max: 240, step: 1, suffix: "sec per loop",
          hint: "Seconds for one full left→right loop. Lower = faster. Default: 45." },
        { path: "logo_strip.line_2", type: "logo_list", label: "Line 2" },
        { path: "logo_strip.line_2_speed", type: "number", label: "Line 2 — scroll speed",
          min: 10, max: 240, step: 1, suffix: "sec per loop",
          hint: "Seconds for one full right→left loop. Lower = faster. Default: 45." },
      ],
    },
    { id: "editorial_collage", label: "Editorial collage",
      description: "In-Motion section with a sticky headline on the left and 5 asymmetric media tiles on the right. Every tile accepts image or video — media is fitted to the designed layout with a center-center spotlight.",
      fields: [
        { path: "editorial_collage.eyebrow", type: "text", label: "Eyebrow" },
        { path: "editorial_collage.headline_main", type: "text", label: "Headline — main",
          hint: "Main words of the headline. The italic and accent words below will be appended to the end." },
        { path: "editorial_collage.headline_italic", type: "text", label: "Headline — italic word",
          hint: "Rendered italic in muted gray." },
        { path: "editorial_collage.headline_accent", type: "text", label: "Headline — accent word",
          hint: "Rendered in brand yellow. A period is appended automatically." },
        { path: "editorial_collage.body", type: "textarea", label: "Body text", rows: 3 },
        { path: "editorial_collage.cta_label", type: "text", label: "CTA label" },
        { path: "editorial_collage.cta_href", type: "url", label: "CTA URL" },
        // 4 stats (tabular numbers). Odd-indexed stats render in brand
        // yellow, even in white — styling is design-locked.
        { path: "editorial_collage.stat_1_value", type: "text", label: "Stat 1 — number" },
        { path: "editorial_collage.stat_1_label", type: "text", label: "Stat 1 — label" },
        { path: "editorial_collage.stat_2_value", type: "text", label: "Stat 2 — number" },
        { path: "editorial_collage.stat_2_label", type: "text", label: "Stat 2 — label" },
        { path: "editorial_collage.stat_3_value", type: "text", label: "Stat 3 — number" },
        { path: "editorial_collage.stat_3_label", type: "text", label: "Stat 3 — label" },
        { path: "editorial_collage.stat_4_value", type: "text", label: "Stat 4 — number" },
        { path: "editorial_collage.stat_4_label", type: "text", label: "Stat 4 — label" },
        // 5 media tiles — fixed count (design is a 5-beat composition).
        { path: "editorial_collage.tile_1_media", type: "media", label: "Tile 1 — media",
          hint: "Square tile (1:1). Recommended: 1400×1400 px or larger. Image or video — fitted to the tile with a center-center spotlight." },
        { path: "editorial_collage.tile_1_tag", type: "text", label: "Tile 1 — caption tag",
          hint: "Optional uppercase label rendered at the bottom of the tile. Leave blank for no caption." },
        { path: "editorial_collage.tile_2_media", type: "media", label: "Tile 2 — media",
          hint: "Wide landscape tile (16:10). Recommended: 1600×1000 px or larger. Image or video." },
        { path: "editorial_collage.tile_2_tag", type: "text", label: "Tile 2 — caption tag" },
        { path: "editorial_collage.tile_3_media", type: "media", label: "Tile 3 — media",
          hint: "Landscape tile (4:3). Recommended: 1600×1200 px or larger. Image or video." },
        { path: "editorial_collage.tile_3_tag", type: "text", label: "Tile 3 — caption tag" },
        { path: "editorial_collage.tile_4_media", type: "media", label: "Tile 4 — media",
          hint: "Landscape tile (4:3). Recommended: 1600×1200 px or larger. Image or video." },
        { path: "editorial_collage.tile_4_tag", type: "text", label: "Tile 4 — caption tag" },
        { path: "editorial_collage.tile_5_media", type: "media", label: "Tile 5 — media",
          hint: "Portrait tile (3:4). Recommended: 1200×1600 px or larger. Image or video." },
        { path: "editorial_collage.tile_5_tag", type: "text", label: "Tile 5 — caption tag" },
      ],
    },
    { id: "development_overview", label: "Development overview",
      description: "Single scroll-revealed paragraph that appears after the Editorial collage. Words reveal word-by-word as the reader scrolls into view.",
      fields: [
        { path: "development_overview.body", type: "textarea", label: "Body text", rows: 5,
          hint: "Plain text. The scroll-reveal animation and large display type are design-locked." },
      ],
    },
    { id: "hero_video",        label: "Hero video",
      description: "Inline storefront-mockup video that sits above the Simple Process section. Replace the video or edit the overlay captions — the play button and layout are design-locked.",
      fields: [
        { path: "hero_video.media", type: "media", label: "Video",
          hint: "Landscape video (16:9). Recommended: 1920×1080 (Full HD) or larger, MP4/WebM. Any image uploaded here will be ignored — this slot is for video only." },
        { path: "hero_video.eyebrow", type: "text", label: "Overlay eyebrow",
          hint: "Small yellow caption at the bottom-left (default: \"Watch · 60s\")." },
        { path: "hero_video.headline", type: "text", label: "Overlay headline",
          hint: "Bold white headline under the eyebrow (default: \"Stop searching. Start shipping.\")." },
        { path: "hero_video.cta_hint", type: "text", label: "Right-side hint",
          hint: "Small uppercase text on the right (default: \"Click to play\"). Hidden on mobile." },
      ],
    },
    { id: "services_grid",     label: "Our Shopify services",
      description: "Vertical accordion section listing every Shopify service. Header text + reorderable list of service tabs (each with its own title, body, highlights, icon and preview media).",
      fields: [
        { path: "services_grid.eyebrow", type: "text", label: "Eyebrow" },
        { path: "services_grid.headline_main", type: "text", label: "Headline — main",
          hint: "Main words of the headline. The accent word below is appended in brand yellow." },
        { path: "services_grid.headline_accent", type: "text", label: "Headline — accent word",
          hint: "Rendered in brand yellow. A period is appended automatically." },
        { path: "services_grid.description", type: "textarea", label: "Description", rows: 3 },
        { path: "services_grid.cta_label", type: "text", label: "CTA label",
          hint: "Right-aligned link in the header (e.g. \"See all services\")." },
        { path: "services_grid.cta_href", type: "url", label: "CTA URL" },
        { path: "services_grid.hover_hint", type: "text", label: "Hover hint",
          hint: "Small caption rendered under the accordion (e.g. \"Hover a service to expand\")." },
        { path: "services_grid.items", type: "service_list", label: "Service tabs",
          hint: "Use ▲ / ▼ to reorder. Click + Edit to expand a tab. Each tab supports a title, body paragraph, highlights, icon and preview media." },
      ],
    },
    { id: "media_background",  label: "Media background",
      description: "Full-bleed video or image background with eyebrow + headline + body caption layered on top. Media is blurred and dimmed for readability.",
      fields: [
        { path: "media_background.eyebrow", type: "text", label: "Eyebrow" },
        { path: "media_background.headline_main", type: "text", label: "Headline — main",
          hint: "Main words of the headline. The italic word below is appended in muted gray." },
        { path: "media_background.headline_italic", type: "text", label: "Headline — italic word",
          hint: "Rendered italic in muted gray." },
        { path: "media_background.headline_tail", type: "text", label: "Headline — tail",
          hint: "Words after the italic word (e.g. \"to scale.\"). A period is NOT appended automatically." },
        { path: "media_background.body", type: "textarea", label: "Body caption", rows: 3 },
        { path: "media_background.media", type: "media", label: "Background media",
          hint: "Full-bleed image or video (16:9). Recommended: 1920×1080 (Full HD) or larger. Media is auto-blurred and dimmed — choose a piece with strong shapes or motion." },
      ],
    },
    { id: "marketing_pinned",  label: "Marketing 360 pinned",
      description: "Pinned horizontal-scroll section that reveals 6 service titles one by one as the user scrolls. Mobile renders as a vertical card stack.",
      fields: [
        { path: "marketing_pinned.eyebrow", type: "text", label: "Eyebrow" },
        { path: "marketing_pinned.headline_main", type: "text", label: "Headline — main",
          hint: "Mobile-only headline. Desktop renders one giant title per panel." },
        { path: "marketing_pinned.headline_accent", type: "text", label: "Headline — accent word",
          hint: "Rendered in brand yellow. A period is appended automatically." },
        { path: "marketing_pinned.cta_primary_label", type: "text", label: "Primary CTA label" },
        { path: "marketing_pinned.cta_primary_href", type: "url", label: "Primary CTA URL" },
        { path: "marketing_pinned.cta_secondary_label", type: "text", label: "Secondary CTA label" },
        { path: "marketing_pinned.cta_secondary_href", type: "url", label: "Secondary CTA URL" },
        { path: "marketing_pinned.panels", type: "panel_list", label: "Panels",
          hint: "Use ▲ / ▼ to reorder. Each panel becomes one full-screen title in the horizontal scroll. Title + body + icon only — design-locked layout otherwise." },
      ],
    },
    { id: "creative_capabilities", label: "Creative capabilities",
      description: "Six-card creative capabilities grid with hover-reveal cross-fading background media. Header pattern matches the Shopify Services section.",
      fields: [
        { path: "creative_capabilities.eyebrow", type: "text", label: "Eyebrow" },
        { path: "creative_capabilities.headline_main", type: "text", label: "Headline — main",
          hint: "Main words of the headline. The italic and accent words below are appended in order." },
        { path: "creative_capabilities.headline_italic", type: "text", label: "Headline — italic word",
          hint: "Rendered italic in muted gray." },
        { path: "creative_capabilities.headline_accent", type: "text", label: "Headline — accent word",
          hint: "Rendered in brand yellow with an underline. A period is appended automatically." },
        { path: "creative_capabilities.description", type: "textarea", label: "Description", rows: 3 },
        { path: "creative_capabilities.cta_label", type: "text", label: "CTA label",
          hint: "Right-aligned link in the header (desktop only)." },
        { path: "creative_capabilities.cta_href", type: "url", label: "CTA URL" },
        { path: "creative_capabilities.items", type: "service_list", variant: "panel", showIcon: true,
          label: "Capability cards",
          itemNoun: "capability", itemNounPlural: "capabilities",
          labels: { title: "Capability title", body: "Capability description", media: "Hover background media" },
          showMedia: true,
          mediaHint: "Full-bleed image or video that fades in behind the grid when this capability is hovered. Recommended: 2000×1125 px or larger.",
          hint: "Use ▲ / ▼ to reorder. Each card has a title, description, icon, and a hover-reveal background image or video." },
      ],
    },
    { id: "case_studies", label: "Case studies",
      description: "Featured client work — sticky scroll-storytelling section with one full-bleed background per project and gigantic counter on the right.",
      fields: [
        { path: "case_studies.eyebrow", type: "text", label: "Eyebrow" },
        { path: "case_studies.headline", type: "text", label: "Headline" },
        { path: "case_studies.description", type: "textarea", label: "Description", rows: 3 },
        { path: "case_studies.cta_label", type: "text", label: "Per-project CTA label",
          hint: "Button under each project (e.g. \"View project\")." },
        { path: "case_studies.cta_href", type: "url", label: "Per-project CTA URL" },
        { path: "case_studies.partner_since_label", type: "text", label: "\"Partner Since\" label",
          hint: "Caption under the partner-since year." },
        { path: "case_studies.completed_label", type: "text", label: "\"Completed\" label",
          hint: "Caption under the tasks-completed number." },
        { path: "case_studies.items", type: "service_list", variant: "panel",
          label: "Projects",
          itemNoun: "project", itemNounPlural: "projects",
          labels: { title: "Brand name", body: "Tagline", media: "Primary image" },
          showHighlights: true, showMedia: true,
          mediaHint: "Square-ish primary project shot (4:5 portrait). Recommended: 1200×1500 px or larger.",
          highlightsRows: 4,
          highlightsPlaceholder:
            "Shopify theme customization\nCustom development features\nStore management\n360° digital services",
          extraFields: [
            { key: "tasks", label: "Tasks completed", type: "text", placeholder: "320+",
              hint: "Big yellow number rendered next to the project. e.g. \"320+\"." },
            { key: "since", label: "Partner since", type: "text", placeholder: "2020",
              hint: "Year — rendered next to the tasks count." },
            { key: "secondary_media", label: "Secondary media", type: "media",
              hint: "Detail / lifestyle shot rendered below the primary image (5:4 landscape). Recommended: 1500×1200 px." },
          ],
          hint: "Use ▲ / ▼ to reorder. Each project shows a primary image, secondary detail shot, tagline, services and stats." },
      ],
    },
    { id: "testimonials", label: "Testimonials",
      description: "Editorial auto-rotating testimonials block. One quote shown at a time with progress-bar tabs at the bottom; hovering pauses the rotation.",
      fields: [
        { path: "testimonials.eyebrow", type: "text", label: "Eyebrow" },
        { path: "testimonials.items", type: "service_list", variant: "panel",
          label: "Testimonials",
          itemNoun: "testimonial", itemNounPlural: "testimonials",
          labels: { title: "Quote", body: "Author name" },
          titleAsTextarea: true,
          extraFields: [
            { key: "role", label: "Role · Brand", type: "text", placeholder: "Owner · sclarandis.com",
              hint: "Format: \"Role · Brand\". Only the brand (right of the \" · \") is shown on the live site, in brand yellow." },
          ],
          defaultItem: { title: "", body: "", role: "" },
          hint: "Use ▲ / ▼ to reorder. Quotes rotate every 5 seconds. Bottom progress bars are auto-generated from this list." },
      ],
    },
    { id: "press_block", label: "Showcase — \"In the press\" headline",
      description: "Site-wide headline shown above each case study's press list (e.g. /showcase/fragrance-veronique-gabai). Empty fields keep the current default copy.",
      fields: [
        { path: "press_block.eyebrow", type: "text", label: "Section eyebrow",
          hint: "Small caption above the headline. Default: \"In the press\"." },
        { path: "press_block.headline_main", type: "text", label: "Headline — main",
          hint: "First white words of the headline. Default: \"The work showed up\"." },
        { path: "press_block.headline_italic", type: "text", label: "Headline — italic words",
          hint: "Italic gray words between main and accent. Default: \"where it\"." },
        { path: "press_block.headline_accent", type: "text", label: "Headline — accent word",
          hint: "Rendered in brand yellow with an underline. A period is appended automatically. Default: \"matters\"." },
      ],
    },
    { id: "faq", label: "FAQ",
      description: "Accordion of common questions. The first item is open by default on first load.",
      fields: [
        { path: "faq.eyebrow", type: "text", label: "Eyebrow" },
        { path: "faq.headline", type: "text", label: "Headline" },
        { path: "faq.description", type: "textarea", label: "Description", rows: 2 },
        { path: "faq.items", type: "service_list", variant: "panel",
          label: "Questions",
          itemNoun: "question", itemNounPlural: "questions",
          labels: { title: "Question", body: "Answer" },
          bodyRows: 4,
          defaultItem: { title: "New question", body: "" },
          hint: "Use ▲ / ▼ to reorder. Click + Edit to expand and edit the question / answer." },
      ],
    },
    { id: "blog_teaser", label: "Blog teaser",
      description: "Home-page block linking to the blog (4 article cards + heading + CTA). The 4 article cards are managed elsewhere — this card edits only the heading and CTA.",
      fields: [
        { path: "blog_teaser.eyebrow", type: "text", label: "Eyebrow",
          hint: "Small yellow caption above the headline. Default: \"From Ideas to Impact\"." },
        { path: "blog_teaser.headline", type: "text", label: "Headline",
          hint: "Big white heading. Default: \"From Ideas to Impact: Explore Our Blog\"." },
        { path: "blog_teaser.body", type: "textarea", label: "Body paragraph", rows: 3,
          hint: "Paragraph under the headline." },
        { path: "blog_teaser.cta_label", type: "text", label: "CTA label",
          hint: "Right-side link text. Default: \"Read our blog\"." },
        { path: "blog_teaser.cta_href", type: "text", label: "CTA link",
          hint: "Where the CTA goes. Default points to the internal /blog page. Use a full https:// URL to open externally." },
      ],
    },
    { id: "closing_cta", label: "Closing CTA",
      description: "Final page-wide call-to-action band — status tag, three-line editorial statement, slow tagline marquee, giant clickable email and contact strip.",
      fields: [
        { path: "closing_cta.status_label", type: "text", label: "Status label",
          hint: "Top-left tag next to the pulsing dot (e.g. \"Still taking new projects\")." },
        { path: "closing_cta.counter_text", type: "text", label: "Counter text",
          hint: "Top-right tabular caption (e.g. \"03 · 26\"). Decorative." },
        { path: "closing_cta.statement_line_1", type: "text", label: "Statement — line 1",
          hint: "First giant line. Rendered uppercase in white." },
        { path: "closing_cta.statement_line_2", type: "text", label: "Statement — line 2",
          hint: "Second line. Rendered serif italic in muted gray." },
        { path: "closing_cta.statement_line_3_main", type: "text", label: "Statement — line 3 (yellow)",
          hint: "Third line — rendered in brand yellow." },
        { path: "closing_cta.statement_line_3_period", type: "text", label: "Statement — line 3 trailing period",
          hint: "Single character rendered in white at the end of the yellow line. Default: \".\"." },
        { path: "closing_cta.signature_brand", type: "text", label: "Signature — brand",
          hint: "Center text in the signature row (e.g. \"Sunday Digital\")." },
        { path: "closing_cta.signature_est", type: "text", label: "Signature — established",
          hint: "Right-of-brand text (e.g. \"Est. 2018\")." },
        { path: "closing_cta.signature_tag", type: "text", label: "Signature — tagline",
          hint: "Last segment in the signature row (e.g. \"A 360° digital studio\")." },
        { path: "closing_cta.tagline_words", type: "service_list", variant: "panel",
          label: "Tagline marquee",
          itemNoun: "word", itemNounPlural: "words",
          labels: { title: "Word" },
          // No body — just the title and a "solid" toggle baked into extraFields.
          showTags: false, showHighlights: false, showMedia: false, showIcon: false,
          extraFields: [
            { key: "solid", label: "Style", type: "text", placeholder: "solid",
              hint: "Type \"solid\" for filled white text, or \"outline\" for outlined text. Defaults to alternating if empty." },
          ],
          defaultItem: { title: "Word", solid: "solid" },
          hint: "Each word becomes one entry in the slow horizontal marquee. Words alternate between solid white and outlined by default — override per word with the Style field." },
        { path: "closing_cta.email_eyebrow", type: "text", label: "Email eyebrow",
          hint: "Small caption above the giant email (e.g. \"Tell us about your project\")." },
        { path: "closing_cta.email", type: "text", label: "Email address",
          hint: "Rendered as a giant clickable mailto link on desktop and a pill button on mobile." },
        { path: "closing_cta.mobile_cta_label", type: "text", label: "Mobile CTA label",
          hint: "Pill-button label shown on mobile only (e.g. \"Write us\")." },
        { path: "closing_cta.location", type: "text", label: "Location",
          hint: "Bottom strip · 1st item (e.g. \"Brooklyn, NY\")." },
        { path: "closing_cta.hours", type: "text", label: "Hours",
          hint: "Bottom strip · 2nd item (e.g. \"Open Mon–Fri\")." },
        { path: "closing_cta.timezone", type: "text", label: "Timezone",
          hint: "Bottom strip · 3rd item (e.g. \"EST\")." },
        { path: "closing_cta.handle_label", type: "text", label: "Social handle label",
          hint: "Bottom strip · 4th item — clickable handle (e.g. \"@sundaydigital\")." },
        { path: "closing_cta.handle_href", type: "url", label: "Social handle URL",
          hint: "Where the handle link goes (e.g. https://instagram.com/sundaydigital)." },
      ],
    },
    { id: "blog_teaser",       label: "Blog teaser",       description: "Most recent blog posts grid." },
  ],
};

// --------------------------- SHOPIFY DEV ---------------------------
export const SHOPIFY_DEV_SCHEMA = {
  key: "shopify-dev",
  label: "Shopify Development",
  route: "/shopify-experts-development",
  sections: [
    {
      id: "hero",
      label: "Hero",
      description: "Top-of-page breadcrumb, eyebrow, two-line headline, sub-headline and primary/secondary CTAs.",
      fields: [
        { path: "hero.breadcrumb", type: "text", label: "Breadcrumb label" },
        { path: "hero.eyebrow", type: "text", label: "Eyebrow" },
        { path: "hero.title_line_1", type: "text", label: "Title — line 1" },
        { path: "hero.title_emphasis", type: "text", label: "Title — italic emphasis word",
          hint: "Rendered italic in muted gray." },
        { path: "hero.title_line_2", type: "text", label: "Title — line 2 (underlined)",
          hint: "Rendered with the brand-yellow underline. A period is appended automatically." },
        { path: "hero.subtitle", type: "textarea", label: "Sub-headline", rows: 3 },
        { path: "hero.cta_primary_label", type: "text", label: "Primary CTA label" },
        { path: "hero.cta_secondary_label", type: "text", label: "Secondary CTA label" },
        { path: "hero.cta_secondary_href", type: "url", label: "Secondary CTA URL" },
      ],
    },
    {
      id: "trust_strip",
      label: "Trust strip",
      description: "Single-row strip of trust-indicator chips under the hero. Each chip is one entry; the last chip is rendered in brand yellow.",
      fields: [
        { path: "trust_strip.items", type: "service_list", variant: "panel",
          label: "Trust chips",
          itemNoun: "chip", itemNounPlural: "chips",
          labels: { title: "Chip label" },
          showBody: false,
          defaultItem: { title: "New chip" },
          hint: "Use ▲ / ▼ to reorder. The last chip is automatically rendered in brand yellow on the live site." },
      ],
    },
    {
      id: "what_you_get",
      label: "What you get",
      description: "Four numbered editorial commitment cards under the trust strip.",
      fields: [
        { path: "what_you_get.eyebrow", type: "text", label: "Eyebrow" },
        { path: "what_you_get.headline_main", type: "text", label: "Headline — main" },
        { path: "what_you_get.headline_italic", type: "text", label: "Headline — italic word",
          hint: "Rendered italic in muted gray." },
        { path: "what_you_get.headline_accent", type: "text", label: "Headline — accent word",
          hint: "Rendered in brand yellow with an underline. A period is appended automatically." },
        { path: "what_you_get.description", type: "textarea", label: "Description", rows: 3 },
        { path: "what_you_get.items", type: "service_list", variant: "panel",
          label: "Commitments",
          itemNoun: "commitment", itemNounPlural: "commitments",
          showTags: true,
          labels: { title: "Commitment title", body: "Body paragraph", tag_number: "Number", tag_label: "(unused)" },
          hint: "Use ▲ / ▼ to reorder. Number is the giant yellow numeral on the card. Tag label is unused for this section." },
      ],
    },
    {
      id: "capabilities",
      label: "Capabilities",
      description: "Vertical accordion of nine Shopify capabilities. Each capability gets a small media tile that fades in when its panel is open.",
      fields: [
        { path: "capabilities.eyebrow", type: "text", label: "Eyebrow" },
        { path: "capabilities.headline_main", type: "text", label: "Headline — main" },
        { path: "capabilities.headline_italic", type: "text", label: "Headline — italic word" },
        { path: "capabilities.headline_accent", type: "text", label: "Headline — accent word",
          hint: "Rendered in brand yellow with an underline. A period is appended automatically." },
        { path: "capabilities.description", type: "textarea", label: "Description", rows: 3 },
        { path: "capabilities.items", type: "service_list", variant: "panel",
          label: "Capabilities",
          itemNoun: "capability", itemNounPlural: "capabilities",
          showTags: true, showMedia: true,
          labels: { title: "Capability title", body: "Capability description", tag_number: "Number", tag_label: "(unused)", media: "Open-state media" },
          mediaHint: "Image or video shown when this capability is open. Recommended: 1400×1050 px or larger.",
          bodyRows: 4,
          hint: "Use ▲ / ▼ to reorder. Number is the small grey label next to the title." },
      ],
    },
    {
      id: "build_showcase",
      label: "Build showcase",
      description: "Full-bleed coding video with overlay copy and three live stats.",
      fields: [
        { path: "build_showcase.eyebrow", type: "text", label: "Eyebrow" },
        { path: "build_showcase.headline_main", type: "text", label: "Headline — main" },
        { path: "build_showcase.headline_italic", type: "text", label: "Headline — italic word" },
        { path: "build_showcase.headline_accent", type: "text", label: "Headline — accent word",
          hint: "Rendered in brand yellow with an underline. A period is appended automatically." },
        { path: "build_showcase.description", type: "textarea", label: "Description", rows: 3 },
        { path: "build_showcase.video", type: "media", label: "Background video",
          hint: "Full-bleed coding/storefront video. Recommended: 1920×1080 (Full HD) MP4/WebM. Image uploads are ignored — this slot is video only." },
        { path: "build_showcase.poster", type: "media", label: "Video poster (fallback image)",
          hint: "Static image shown while the video loads or when video is unsupported. Recommended: 2000×1125 px." },
        { path: "build_showcase.stats", type: "service_list", variant: "panel",
          label: "Stats",
          itemNoun: "stat", itemNounPlural: "stats",
          labels: { title: "Value", body: "Label" },
          bodyRows: 1,
          defaultItem: { title: "100%", body: "Custom-coded" },
          hint: "Three stats render in a single row under the headline. Value is the giant yellow number; label is the small uppercase caption." },
      ],
    },
    {
      id: "possibilities",
      label: "Possibilities",
      description: "Sticky-stacking section: editorial intro followed by 6 buckets of feature lists. The interactive mini-demo widgets next to each bucket are design-locked and indexed by position 0–5.",
      fields: [
        { path: "possibilities.eyebrow", type: "text", label: "Intro eyebrow" },
        { path: "possibilities.headline_main", type: "text", label: "Intro headline — main" },
        { path: "possibilities.headline_italic", type: "text", label: "Intro headline — italic word" },
        { path: "possibilities.headline_accent", type: "text", label: "Intro headline — accent word",
          hint: "Rendered in brand yellow with an underline. A period is appended automatically." },
        { path: "possibilities.description", type: "textarea", label: "Intro description", rows: 3 },
        { path: "possibilities.buckets", type: "service_list", variant: "panel",
          label: "Buckets",
          itemNoun: "bucket", itemNounPlural: "buckets",
          showHighlights: true, showTags: true,
          labels: { title: "Bucket title", body: "(unused)", tag_number: "Number",
            tag_label: "Bucket eyebrow", highlights: "Features · one per line" },
          showBody: false, highlightsRows: 8,
          highlightsPlaceholder:
            "Product bundle kits\nCustom product configurators\nTiered pricing rules",
          hint: "Use ▲ / ▼ to reorder. The interactive mini-demo on the right is design-locked and indexed by position (0–5) — moving a bucket also moves which demo it pairs with." },
      ],
    },
    {
      id: "process",
      label: "Process",
      description: "Sticky-pinned vertical timeline (desktop) / stacked editorial cards (mobile). Seven engagement steps.",
      fields: [
        { path: "process.eyebrow", type: "text", label: "Eyebrow" },
        { path: "process.headline_main", type: "text", label: "Headline — main" },
        { path: "process.headline_italic", type: "text", label: "Headline — italic word" },
        { path: "process.headline_accent", type: "text", label: "Headline — accent word",
          hint: "Rendered in brand yellow with an underline. A period is appended automatically." },
        { path: "process.description", type: "textarea", label: "Description", rows: 3 },
        { path: "process.items", type: "service_list", variant: "panel",
          label: "Steps",
          itemNoun: "step", itemNounPlural: "steps",
          showTags: true,
          labels: { title: "Step title", body: "Step description", tag_number: "Number", tag_label: "(unused)" },
          bodyRows: 3,
          hint: "Use ▲ / ▼ to reorder. Number is the small chip / yellow numeral. Title appears in the active state on the right column." },
      ],
    },
    {
      id: "tech_stack",
      label: "Tech stack",
      description: "Eyebrow + headline + slow horizontal marquee of 16 technology names.",
      fields: [
        { path: "tech_stack.eyebrow", type: "text", label: "Eyebrow" },
        { path: "tech_stack.headline_main", type: "text", label: "Headline — main" },
        { path: "tech_stack.headline_italic", type: "text", label: "Headline — italic word",
          hint: "Rendered italic in muted gray. A period is appended automatically." },
        { path: "tech_stack.items", type: "service_list", variant: "panel",
          label: "Technologies",
          itemNoun: "technology", itemNounPlural: "technologies",
          labels: { title: "Technology name" },
          showBody: false,
          hint: "Use ▲ / ▼ to reorder. Each entry becomes one word in the slow horizontal marquee." },
      ],
    },
    {
      id: "who_we_work_with",
      label: "Who we work with",
      description: "Typographic statement block — eyebrow + multi-part headline + body + CTA. Opens the contact panel on click.",
      fields: [
        { path: "who_we_work_with.eyebrow", type: "text", label: "Eyebrow" },
        { path: "who_we_work_with.headline_part_1", type: "text", label: "Headline — part 1" },
        { path: "who_we_work_with.headline_italic", type: "text", label: "Headline — italic word",
          hint: "Rendered italic in muted gray." },
        { path: "who_we_work_with.headline_part_2", type: "text", label: "Headline — part 2",
          hint: "Words after the italic word and before the accent word." },
        { path: "who_we_work_with.headline_accent", type: "text", label: "Headline — accent word",
          hint: "Rendered in brand yellow with an underline." },
        { path: "who_we_work_with.headline_part_3", type: "text", label: "Headline — tail",
          hint: "Final words after the accent. A period is NOT appended — type your own." },
        { path: "who_we_work_with.description", type: "textarea", label: "Description", rows: 3 },
        { path: "who_we_work_with.cta_label", type: "text", label: "CTA label" },
      ],
    },
    { id: "closing_cta", label: "Closing CTA",
      description: "Final page-wide call-to-action band — currently shared with the home page (edit it from /admin/content/home → Closing CTA)." },
  ],
};

// --------------------------- MARKETING 360 ---------------------------
export const MARKETING_SCHEMA = {
  key: "marketing",
  label: "Marketing 360",
  route: "/ecommerce-marketing-agency",
  sections: [
    {
      id: "hero",
      label: "Hero",
      description: "Top-of-page breadcrumb, eyebrow, two-line headline (with italic emphasis), sub-headline and primary/secondary CTAs.",
      fields: [
        { path: "hero.breadcrumb", type: "text", label: "Breadcrumb label" },
        { path: "hero.eyebrow", type: "text", label: "Eyebrow" },
        { path: "hero.title_line_1", type: "text", label: "Title — line 1" },
        { path: "hero.title_line_2", type: "text", label: "Title — line 2 prefix",
          hint: "Text rendered before the italic emphasis word on the second line." },
        { path: "hero.title_emphasis", type: "text", label: "Title — italic emphasis word",
          hint: "Rendered italic in muted gray." },
        { path: "hero.title_accent", type: "text", label: "Title — accent word (optional)",
          hint: "Rendered in brand yellow with an underline. If left empty, just a period is appended after the italic word." },
        { path: "hero.subtitle", type: "textarea", label: "Sub-headline", rows: 3 },
        { path: "hero.cta_primary_label", type: "text", label: "Primary CTA label" },
        { path: "hero.cta_secondary_label", type: "text", label: "Secondary CTA label" },
        { path: "hero.cta_secondary_href", type: "url", label: "Secondary CTA URL" },
      ],
    },
    {
      id: "trust_strip",
      label: "Trust strip",
      description: "Single-row strip of trust-indicator chips under the hero. The last chip is rendered in brand yellow.",
      fields: [
        { path: "trust_strip.items", type: "service_list", variant: "panel",
          label: "Trust chips",
          itemNoun: "chip", itemNounPlural: "chips",
          labels: { title: "Chip label" },
          showBody: false,
          defaultItem: { title: "New chip" },
          hint: "Use ▲ / ▼ to reorder. The last chip is automatically rendered in brand yellow on the live site." },
      ],
    },
    {
      id: "why_us",
      label: "Why us",
      description: "Twin-column editorial paragraph block under the trust strip — eyebrow, multi-part headline, plus two side-by-side title + body columns.",
      fields: [
        { path: "why_us.eyebrow", type: "text", label: "Eyebrow" },
        { path: "why_us.headline_main", type: "text", label: "Headline — main",
          hint: "First chunk of the headline (e.g. \"Strategy meets\")." },
        { path: "why_us.headline_italic", type: "text", label: "Headline — italic word" },
        { path: "why_us.headline_middle", type: "text", label: "Headline — middle text",
          hint: "Text rendered between the italic word and the accent (e.g. \"Data\")." },
        { path: "why_us.headline_accent", type: "text", label: "Headline — accent word",
          hint: "Rendered in brand yellow with an underline." },
        { path: "why_us.headline_tail", type: "text", label: "Headline — tail",
          hint: "Final words after the accent (e.g. \"meets craft.\"). A period is NOT appended automatically." },
        { path: "why_us.columns", type: "service_list", variant: "panel",
          label: "Columns",
          itemNoun: "column", itemNounPlural: "columns",
          labels: { title: "Column title", body: "Column body" },
          bodyRows: 4,
          hint: "Two side-by-side editorial columns. Use ▲ / ▼ to reorder. Each column has a title + supporting paragraph." },
      ],
    },
    {
      id: "method",
      label: "Our method",
      description: "Sticky-stacking 6-step method module — intro header followed by 3D-tilting cards. Each card has a number, eyebrow, title, body and a list of deliverables (KPI markers are auto-cycled).",
      fields: [
        { path: "method.intro_eyebrow", type: "text", label: "Intro eyebrow" },
        { path: "method.headline_main", type: "text", label: "Intro headline — main" },
        { path: "method.headline_italic", type: "text", label: "Intro headline — italic word" },
        { path: "method.headline_accent", type: "text", label: "Intro headline — accent word",
          hint: "Rendered in brand yellow with an underline. A period is appended automatically." },
        { path: "method.intro_description", type: "textarea", label: "Intro description", rows: 3 },
        { path: "method.kpi_label", type: "text", label: "Card sidebar label",
          hint: "Small uppercase caption above the deliverables list (e.g. \"What ships at this stage\")." },
        { path: "method.items", type: "service_list", variant: "panel",
          label: "Method steps",
          itemNoun: "step", itemNounPlural: "steps",
          showTags: true, showHighlights: true,
          labels: { title: "Step title", body: "Step body", tag_number: "Number", tag_label: "Step eyebrow", highlights: "Deliverables · one per line" },
          bodyRows: 3, highlightsRows: 4,
          highlightsPlaceholder: "Audit & analytics\nAudience research\nGoal alignment",
          hint: "Use ▲ / ▼ to reorder. Each step is a sticky card with a giant numeral, eyebrow chip, title, body, and a 'what ships' deliverables list." },
      ],
    },
    {
      id: "services_grid",
      label: "Services",
      description: "Eight-card editorial grid (paid / organic / email / creative). Cards alternate vertical offset; each has a hover image, number, title and body. Each card deep-links to itself via id `service-XX` for the hero ring.",
      fields: [
        { path: "services_grid.eyebrow", type: "text", label: "Eyebrow" },
        { path: "services_grid.headline_main", type: "text", label: "Headline — main" },
        { path: "services_grid.headline_italic", type: "text", label: "Headline — italic word" },
        { path: "services_grid.headline_accent", type: "text", label: "Headline — accent word",
          hint: "Rendered in brand yellow with an underline. A period is appended automatically." },
        { path: "services_grid.description", type: "textarea", label: "Description", rows: 3 },
        { path: "services_grid.cta_href", type: "url", label: "Per-service link target",
          hint: "All 8 cards link to this URL on click (e.g. \"#contact\")." },
        { path: "services_grid.items", type: "service_list", variant: "panel",
          label: "Services",
          itemNoun: "service", itemNounPlural: "services",
          showTags: true, showMedia: true,
          labels: { title: "Service title", body: "Service body", tag_number: "Number", tag_label: "(unused)", media: "Hover image" },
          mediaHint: "4:3 image rendered as the card cover. Recommended: 1200×900 px or larger.",
          bodyRows: 3,
          hint: "Use ▲ / ▼ to reorder. Number is the small chip in the corner. The hero ring node N links to service N — keep the ring labels and service order in sync if you reorder." },
      ],
    },
    {
      id: "showreel",
      label: "Showreel",
      description: "Full-bleed background video band — eyebrow, multi-part headline and supporting copy.",
      fields: [
        { path: "showreel.eyebrow", type: "text", label: "Eyebrow" },
        { path: "showreel.headline_main", type: "text", label: "Headline — main" },
        { path: "showreel.headline_italic", type: "text", label: "Headline — italic word" },
        { path: "showreel.headline_accent", type: "text", label: "Headline — accent word",
          hint: "Rendered in brand yellow with an underline. A period is appended automatically." },
        { path: "showreel.description", type: "textarea", label: "Description", rows: 3 },
        { path: "showreel.video", type: "media", label: "Background media (image or video)",
          hint: "Full-bleed marketing reel. Recommended: 1920×1080 (Full HD) MP4/WebM, OR a high-res still image (PNG/JPG/WebP). Detected automatically and rendered accordingly." },
        { path: "showreel.poster", type: "media", label: "Video poster (used only when the slot above is a video)",
          hint: "Static image shown while the video loads. Recommended: 2000×1125 px. Ignored when an image is used in Background media." },
      ],
    },
    { id: "closing_cta",   label: "Closing CTA",
      description: "Final page-wide call-to-action band — shared with the home page (edit it from /admin/content/home → Closing CTA)." },
  ],
};

// --------------------------- CREATIVE ---------------------------
export const CREATIVE_SCHEMA = {
  key: "creative",
  label: "Creative & Design",
  route: "/creative",
  sections: [
    {
      id: "hero",
      label: "Hero",
      description: "Full-bleed video hero — breadcrumb, eyebrow, two-line headline with a rotating verb, sub-headline, primary/secondary CTAs, and a bottom marquee.",
      fields: [
        { path: "hero.breadcrumb", type: "text", label: "Breadcrumb label" },
        { path: "hero.eyebrow", type: "text", label: "Eyebrow" },
        { path: "hero.video", type: "media", label: "Background video",
          hint: "Full-bleed cinematic reel. Recommended: 1920×1080 (Full HD) MP4/WebM. Image uploads are ignored — this slot is video only." },
        { path: "hero.poster", type: "media", label: "Video poster (fallback image)",
          hint: "Static image shown while the video loads. Recommended: 2000×1125 px." },
        { path: "hero.title_line_1", type: "text", label: "Title — line 1" },
        { path: "hero.title_line_2", type: "text", label: "Title — line 2 prefix",
          hint: "Text rendered before the rotating verb on the second line (e.g. \"built to\")." },
        { path: "hero.verbs", type: "service_list", variant: "panel",
          label: "Rotating verbs",
          itemNoun: "verb", itemNounPlural: "verbs",
          labels: { title: "Verb" },
          showBody: false,
          defaultItem: { title: "New verb" },
          hint: "Words that cycle vertically on the hero (every 3.4s). Defaults: Connect, Captivate, Convert. A period is appended automatically." },
        { path: "hero.subtitle", type: "textarea", label: "Sub-headline", rows: 3 },
        { path: "hero.cta_primary_label", type: "text", label: "Primary CTA label" },
        { path: "hero.cta_secondary_label", type: "text", label: "Secondary CTA label" },
        { path: "hero.cta_secondary_href", type: "url", label: "Secondary CTA URL" },
        { path: "hero.side_caption", type: "text", label: "Right-rail caption (desktop)",
          hint: "Vertical caption on the right edge of the hero (desktop only)." },
        { path: "hero.marquee_words", type: "service_list", variant: "panel",
          label: "Bottom marquee words",
          itemNoun: "word", itemNounPlural: "words",
          labels: { title: "Word" },
          showBody: false,
          defaultItem: { title: "New word" },
          hint: "Slow scrolling strip under the hero. Each entry is one word." },
      ],
    },
    {
      id: "tailored_solutions",
      label: "Tailored solutions",
      description: "Horizontal pinned scroll — four full-screen editorial panels that reveal as the user scrolls. Each panel has a giant numeral, image half, and copy half.",
      fields: [
        { path: "tailored_solutions.progress_label", type: "text", label: "Top-bar label",
          hint: "Small uppercase caption pinned to the top of the scroll (e.g. \"Reel — Tailored solutions\")." },
        { path: "tailored_solutions.items", type: "service_list", variant: "panel",
          label: "Solution panels",
          itemNoun: "panel", itemNounPlural: "panels",
          showTags: true, showMedia: true,
          labels: { title: "Panel title", body: "Panel body", tag_number: "Number", tag_label: "Panel eyebrow", media: "Panel image" },
          mediaHint: "Full-bleed landscape image. Recommended: 1800×2250 px or larger.",
          bodyRows: 4,
          extraFields: [
            { key: "tag", label: "Tagline (italic yellow)", type: "text",
              hint: "Short italic yellow caption at the bottom of the panel (e.g. \"No limits, just results.\")." },
          ],
          hint: "Use ▲ / ▼ to reorder. Each panel gets its own full-screen step in the horizontal scroll." },
      ],
    },
    {
      id: "stats_band",
      label: "Stats band",
      description: "Editorial band with section header + three count-up stats.",
      fields: [
        { path: "stats_band.eyebrow", type: "text", label: "Eyebrow" },
        { path: "stats_band.headline_main", type: "text", label: "Headline — main" },
        { path: "stats_band.headline_italic", type: "text", label: "Headline — italic word" },
        { path: "stats_band.headline_accent", type: "text", label: "Headline — accent word",
          hint: "Rendered in brand yellow with an underline. A period is appended automatically." },
        { path: "stats_band.description", type: "textarea", label: "Description", rows: 3 },
        { path: "stats_band.items", type: "service_list", variant: "panel",
          label: "Stats",
          itemNoun: "stat", itemNounPlural: "stats",
          labels: { title: "Value (integer)", body: "Label" },
          bodyRows: 1,
          extraFields: [
            { key: "suffix", label: "Suffix", type: "text", placeholder: "+",
              hint: "Optional suffix rendered after the value (e.g. \"+\" or \"k\"). Leave blank for none." },
          ],
          defaultItem: { title: "10", body: "Years", suffix: "+" },
          hint: "Integers only — the number counts up on viewport enter. Use the Suffix field for a trailing symbol." },
      ],
    },
    {
      id: "possibilities_lab",
      label: "Possibilities lab",
      description: "Asymmetric 6-tile grid — each tile plays a hover video. Span classes control the grid layout.",
      fields: [
        { path: "possibilities_lab.eyebrow", type: "text", label: "Eyebrow" },
        { path: "possibilities_lab.headline_main", type: "text", label: "Headline — main" },
        { path: "possibilities_lab.headline_italic", type: "text", label: "Headline — italic word" },
        { path: "possibilities_lab.headline_accent", type: "text", label: "Headline — accent word",
          hint: "Rendered in brand yellow with an underline. A period is appended automatically." },
        { path: "possibilities_lab.description", type: "textarea", label: "Description", rows: 3 },
        { path: "possibilities_lab.items", type: "service_list", variant: "panel",
          label: "Possibility tiles",
          itemNoun: "tile", itemNounPlural: "tiles",
          showTags: true, showMedia: true,
          labels: { title: "Tile title", body: "Tile body", tag_number: "Number", tag_label: "(unused)", media: "Poster image" },
          mediaHint: "Poster image shown before hover. Recommended: 1200×900 px or larger.",
          extraFields: [
            { key: "video", label: "Hover video", type: "media",
              hint: "Plays on hover when the visitor lands on the tile. Upload an MP4 / WebM (recommended ≤ 8 MB, 1280×720 or smaller) or paste a URL." },
            { key: "span", label: "Grid span class", type: "text",
              placeholder: "lg:col-span-5 lg:row-span-2",
              hint: "Tailwind classes that define the tile's layout size. Defaults shown — mix and match lg:col-span-N / lg:row-span-N." },
          ],
          hint: "Use ▲ / ▼ to reorder. Each tile has a poster image + a hover video — both are editable. Grid span controls the shape; keep a balanced mix if you swap tiles." },
      ],
    },
    {
      id: "more_services",
      label: "More services",
      description: "Four alternating parallax service panels, each with a large image, eyebrow, title, body and CTA button.",
      fields: [
        { path: "more_services.eyebrow", type: "text", label: "Eyebrow" },
        { path: "more_services.headline_main", type: "text", label: "Headline — main" },
        { path: "more_services.headline_italic", type: "text", label: "Headline — italic word" },
        { path: "more_services.headline_accent", type: "text", label: "Headline — accent word",
          hint: "Rendered in brand yellow with an underline. A period is appended automatically." },
        { path: "more_services.items", type: "service_list", variant: "panel",
          label: "Services",
          itemNoun: "service", itemNounPlural: "services",
          showTags: true, showMedia: true,
          labels: { title: "Service title", body: "Service body", tag_number: "Number", tag_label: "Service eyebrow", media: "Panel image" },
          mediaHint: "Landscape image. Recommended: 1600×2000 px or larger.",
          bodyRows: 4,
          extraFields: [
            { key: "cta", label: "CTA button label", type: "text", placeholder: "Discover solutions",
              hint: "Small yellow text-button under the body. Opens the contact panel on click." },
          ],
          hint: "Use ▲ / ▼ to reorder. Panels alternate left/right automatically based on position." },
      ],
    },
    {
      id: "latest_projects",
      label: "Latest projects",
      description: "Trophy grid of 4 recent projects — each with title, tagline and cover image. Links to an optional URL.",
      fields: [
        { path: "latest_projects.eyebrow", type: "text", label: "Eyebrow" },
        { path: "latest_projects.headline_main", type: "text", label: "Headline — main" },
        { path: "latest_projects.headline_italic", type: "text", label: "Headline — italic word" },
        { path: "latest_projects.headline_accent", type: "text", label: "Headline — accent word",
          hint: "Rendered in brand yellow with an underline. A period is appended automatically." },
        { path: "latest_projects.cta_label", type: "text", label: "Top-right CTA label",
          hint: "Yellow text-link in the section header (e.g. \"All projects\")." },
        { path: "latest_projects.cta_href", type: "url", label: "Top-right CTA URL",
          hint: "Default: #contact" },
        { path: "latest_projects.items", type: "service_list", variant: "panel",
          label: "Projects",
          itemNoun: "project", itemNounPlural: "projects",
          showTags: true, showMedia: true,
          labels: { title: "Project title", body: "Tagline (italic)", tag_number: "Number", tag_label: "(unused)", media: "Cover image" },
          mediaHint: "Portrait cover shot. Recommended: 1400×1750 px or larger.",
          bodyRows: 2,
          extraFields: [
            { key: "href", label: "Project URL", type: "url", placeholder: "#",
              hint: "Link target for the project card. Defaults to # if empty." },
          ],
          hint: "Use ▲ / ▼ to reorder. Every other card is offset downward automatically (masonry rhythm)." },
      ],
    },
    {
      id: "cross_page_browser",
      label: "Cross-page browser",
      description: "Mosaic of 5 tiles that link to sister pages (or open the contact panel). First tile is wide by default.",
      fields: [
        { path: "cross_page_browser.eyebrow", type: "text", label: "Eyebrow" },
        { path: "cross_page_browser.headline_main", type: "text", label: "Headline — main" },
        { path: "cross_page_browser.headline_italic", type: "text", label: "Headline — italic word" },
        { path: "cross_page_browser.headline_accent", type: "text", label: "Headline — accent word",
          hint: "Rendered in brand yellow with an underline. A question mark (\"?\") is appended automatically." },
        { path: "cross_page_browser.description", type: "textarea", label: "Description", rows: 2 },
        { path: "cross_page_browser.items", type: "service_list", variant: "panel",
          label: "Tiles",
          itemNoun: "tile", itemNounPlural: "tiles",
          showTags: false, showMedia: true,
          labels: { title: "Tile label (big)", body: "Tile line (italic)", media: "Tile image" },
          bodyRows: 2,
          mediaHint: "Background image. Recommended: 1200×900 px or larger.",
          extraFields: [
            { key: "href", label: "Link target", type: "url", placeholder: "/",
              hint: "Internal route (e.g. /shopify-experts-development) or anchor (e.g. #contact)." },
            { key: "is_cta", label: "Is contact CTA?", type: "text", placeholder: "yes / no",
              hint: "Type \"yes\" to make this tile open the contact panel instead of navigating. Any other value = regular link." },
            { key: "span", label: "Grid span class", type: "text", placeholder: "lg:col-span-4",
              hint: "Tailwind classes that define the tile's layout size." },
          ],
          hint: "Use ▲ / ▼ to reorder. First tile is typically extra-wide (col-span-4 + row-span-2)." },
      ],
    },
    { id: "closing_cta",          label: "Closing CTA",
      description: "Final page-wide call-to-action band — shared with the home page (edit it from /admin/content/home → Closing CTA)." },
  ],
};

// --------------------------- ABOUT ---------------------------
export const ABOUT_SCHEMA = {
  key: "about",
  label: "Our Story",
  route: "/about",
  sections: [
    {
      id: "hero",
      label: "Hero",
      description: "Editorial hero — breadcrumb, eyebrow, giant \"Our Story.\" headline, subtitle, primary / secondary CTAs, a photo collage, centered badge, and bottom marquee.",
      fields: [
        { path: "hero.breadcrumb", type: "text", label: "Breadcrumb label" },
        { path: "hero.eyebrow", type: "text", label: "Eyebrow" },
        { path: "hero.title_line_1", type: "text", label: "Title — line 1" },
        { path: "hero.title_line_2", type: "text", label: "Title — line 2 (italic yellow)",
          hint: "Rendered italic in brand yellow. A period is appended automatically." },
        { path: "hero.subtitle", type: "textarea", label: "Sub-headline", rows: 4 },
        { path: "hero.cta_primary_label", type: "text", label: "Primary CTA label" },
        { path: "hero.cta_secondary_label", type: "text", label: "Secondary CTA label" },
        { path: "hero.cta_secondary_href", type: "url", label: "Secondary CTA URL" },
        { path: "hero.photo_back", type: "media", label: "Back photo",
          hint: "Lower-left photo in the collage. Recommended: 900×1200 px (portrait)." },
        { path: "hero.photo_front", type: "media", label: "Front photo",
          hint: "Upper-right photo in the collage. Recommended: 900×1200 px (portrait)." },
        { path: "hero.badge_number", type: "text", label: "Badge symbol",
          hint: "Character in the yellow circle (e.g. \"+\")." },
        { path: "hero.badge_caption", type: "text", label: "Badge caption",
          hint: "Small uppercase caption under the badge symbol (e.g. \"Two · One\")." },
        { path: "hero.marquee_words", type: "service_list", variant: "panel",
          label: "Bottom marquee words",
          itemNoun: "word", itemNounPlural: "words",
          labels: { title: "Word" },
          showBody: false,
          defaultItem: { title: "New word" },
          hint: "Running statement at the bottom of the hero. Each entry is one word." },
      ],
    },
    {
      id: "origin",
      label: "Origin story",
      description: "Full editorial paragraph with pull-quote and stat. Left column has the section anchor and teaser, right column holds the long-form copy.",
      fields: [
        { path: "origin.eyebrow", type: "text", label: "Sidebar eyebrow" },
        { path: "origin.headline_main", type: "text", label: "Sidebar headline — main" },
        { path: "origin.headline_italic", type: "text", label: "Sidebar headline — italic word" },
        { path: "origin.headline_accent", type: "text", label: "Sidebar headline — accent word",
          hint: "Rendered in brand yellow with an underline. A period is appended automatically." },
        { path: "origin.sidebar_description", type: "textarea", label: "Sidebar description", rows: 3 },
        { path: "origin.intro_part_1", type: "textarea", label: "Intro — part 1", rows: 3,
          hint: "First chunk of the giant intro paragraph (white editorial display type)." },
        { path: "origin.intro_italic", type: "text", label: "Intro — italic phrase",
          hint: "Rendered italic in muted gray." },
        { path: "origin.intro_accent", type: "text", label: "Intro — accent phrase",
          hint: "Rendered in brand yellow with an underline." },
        { path: "origin.intro_part_2", type: "textarea", label: "Intro — tail", rows: 3,
          hint: "Final chunk of the giant intro paragraph (after the accent phrase)." },
        { path: "origin.paragraph_1", type: "textarea", label: "Body paragraph 1", rows: 4 },
        { path: "origin.paragraph_2", type: "textarea", label: "Body paragraph 2", rows: 4 },
        { path: "origin.stat_value", type: "text", label: "Pull-stat value",
          hint: "Giant yellow number at the bottom (e.g. \"500\")." },
        { path: "origin.stat_suffix", type: "text", label: "Pull-stat suffix",
          hint: "Smaller symbol after the number (e.g. \"+\")." },
        { path: "origin.stat_label", type: "text", label: "Pull-stat caption",
          hint: "Uppercase caption alongside the pull-stat." },
      ],
    },
    {
      id: "stats_ribbon",
      label: "Stats ribbon",
      description: "Compact 4-stat ribbon under the origin section.",
      fields: [
        { path: "stats_ribbon.items", type: "service_list", variant: "panel",
          label: "Stats",
          itemNoun: "stat", itemNounPlural: "stats",
          labels: { title: "Value", body: "Label" },
          bodyRows: 1,
          extraFields: [
            { key: "suffix", label: "Suffix (yellow)", type: "text", placeholder: "+",
              hint: "Rendered in brand yellow after the value (e.g. \"+\", \"→1\")." },
          ],
          defaultItem: { title: "25", body: "Years of combined craft", suffix: "+" },
          hint: "Use ▲ / ▼ to reorder. Exactly 4 stats look best in the 4-column layout." },
      ],
    },
    {
      id: "they_trust_us",
      label: "They trust us",
      description: "\"The Index\" — numbered list of partners on the left with a sticky image preview on the right. Hover a row to cross-fade the preview.",
      fields: [
        { path: "they_trust_us.eyebrow", type: "text", label: "Eyebrow" },
        { path: "they_trust_us.headline_main", type: "text", label: "Headline — main" },
        { path: "they_trust_us.headline_italic", type: "text", label: "Headline — italic word" },
        { path: "they_trust_us.headline_accent", type: "text", label: "Headline — accent word",
          hint: "Rendered in brand yellow with an underline. A period is appended automatically." },
        { path: "they_trust_us.description", type: "textarea", label: "Description", rows: 3 },
        { path: "they_trust_us.caption_left", type: "text", label: "Column caption — left" },
        { path: "they_trust_us.caption_right", type: "text", label: "Column caption — right" },
        { path: "they_trust_us.footer_right", type: "text", label: "Footer — right",
          hint: "Text below the list (e.g. \"Selected work · 2018 — 2025\")." },
        { path: "they_trust_us.preview_caption", type: "text", label: "Preview caption",
          hint: "Small caption under the sticky preview." },
        { path: "they_trust_us.items", type: "service_list", variant: "panel",
          label: "Partners",
          itemNoun: "partner", itemNounPlural: "partners",
          showMedia: true,
          labels: { title: "Brand name", body: "Note (italic)", media: "Partner image" },
          mediaHint: "Portrait cover image. Recommended: 1400×1750 px.",
          bodyRows: 2,
          extraFields: [
            { key: "discipline", label: "Discipline", type: "text", placeholder: "Fragrance",
              hint: "Short discipline caption rendered next to the brand name." },
            { key: "year", label: "Year", type: "text", placeholder: "2019",
              hint: "Partner-since year rendered after the discipline." },
          ],
          hint: "Use ▲ / ▼ to reorder. Rows are numbered automatically by position." },
      ],
    },
    {
      id: "how_we_do_it",
      label: "How we do it",
      description: "Six editorial \"tenet\" spreads, each with a giant numeral, image, title, body and italic credo.",
      fields: [
        { path: "how_we_do_it.eyebrow", type: "text", label: "Eyebrow" },
        { path: "how_we_do_it.headline_main", type: "text", label: "Headline — main" },
        { path: "how_we_do_it.headline_italic", type: "text", label: "Headline — italic word" },
        { path: "how_we_do_it.headline_accent", type: "text", label: "Headline — accent word",
          hint: "Rendered in brand yellow with an underline. A period is appended automatically." },
        { path: "how_we_do_it.description", type: "textarea", label: "Description", rows: 3 },
        { path: "how_we_do_it.items", type: "service_list", variant: "panel",
          label: "Tenets",
          itemNoun: "tenet", itemNounPlural: "tenets",
          showTags: true, showMedia: true,
          labels: { title: "Tenet title", body: "Tenet body", tag_number: "Number", tag_label: "Tenet eyebrow", media: "Mood image" },
          mediaHint: "Landscape image. Recommended: 1400×1750 px.",
          bodyRows: 4,
          extraFields: [
            { key: "credo", label: "Credo (italic yellow)", type: "text",
              hint: "Short italic yellow credo rendered below the body (e.g. \"Happy clients. Repeat clients.\")." },
          ],
          hint: "Use ▲ / ▼ to reorder. Spreads alternate left/right automatically based on position." },
      ],
    },
    {
      id: "manifesto",
      label: "Manifesto",
      description: "Final editorial pull-quote with two italic emphasis spans and a yellow accent phrase. Two CTAs under the quote.",
      fields: [
        { path: "manifesto.eyebrow", type: "text", label: "Eyebrow" },
        { path: "manifesto.quote_part_1", type: "textarea", label: "Quote — part 1", rows: 2 },
        { path: "manifesto.italic_1", type: "text", label: "Italic phrase 1" },
        { path: "manifesto.quote_part_2", type: "textarea", label: "Quote — part 2", rows: 2,
          hint: "Text between the first italic phrase and the accent phrase." },
        { path: "manifesto.accent_phrase", type: "text", label: "Accent phrase",
          hint: "Rendered in brand yellow with an underline." },
        { path: "manifesto.quote_part_3", type: "textarea", label: "Quote — part 3", rows: 2,
          hint: "Text between the accent phrase and the second italic phrase." },
        { path: "manifesto.italic_2", type: "text", label: "Italic phrase 2" },
        { path: "manifesto.quote_part_4", type: "textarea", label: "Quote — tail", rows: 2 },
        { path: "manifesto.cta_primary_label", type: "text", label: "Primary CTA label" },
        { path: "manifesto.cta_secondary_label", type: "text", label: "Secondary CTA label" },
        { path: "manifesto.cta_secondary_href", type: "url", label: "Secondary CTA URL",
          hint: "Use `#contact` to open the Contact panel, or any URL/path." },
      ],
    },
    { id: "closing_cta",  label: "Closing CTA",
      description: "Final page-wide call-to-action band — shared with the home page (edit it from /admin/content/home → Closing CTA)." },
    { id: "testimonials", label: "Testimonials",
      description: "Independent /about testimonials block (renders below \"They Trust Us\"). Same auto-rotating layout as the home page — content is separately curated. Empty list = section auto-hidden on /about.",
      fields: [
        { path: "testimonials.eyebrow", type: "text", label: "Eyebrow" },
        { path: "testimonials.items", type: "service_list", variant: "panel",
          label: "Testimonials",
          itemNoun: "testimonial", itemNounPlural: "testimonials",
          labels: { title: "Quote", body: "Author name" },
          titleAsTextarea: true,
          extraFields: [
            { key: "role", label: "Role · Brand", type: "text", placeholder: "Owner · sclarandis.com",
              hint: "Format: \"Role · Brand\". Only the brand (right of the \" · \") is shown on the live site, in brand yellow." },
          ],
          defaultItem: { title: "", body: "", role: "" },
          hint: "Use ▲ / ▼ to reorder. Quotes rotate every 5 seconds. Leave empty to hide the block." },
      ],
    },
  ],
};

// --------------------------- CONTACT ---------------------------
export const CONTACT_SCHEMA = {
  key: "contact",
  label: "Contact",
  route: "/contact",
  sections: [
    {
      id: "hero",
      label: "Hero",
      description:
        "Editorial hero — breadcrumb, eyebrow, giant \"Let's talk.\" headline, supporting paragraph, primary CTA.",
      fields: [
        { path: "hero.breadcrumb", type: "text", label: "Breadcrumb label" },
        { path: "hero.eyebrow", type: "text", label: "Eyebrow",
          hint: "Small uppercase label above the headline." },
        { path: "hero.title_line_1", type: "text", label: "Title — line 1" },
        { path: "hero.title_line_2", type: "text", label: "Title — line 2 (italic)",
          hint: "Rendered italic in muted gray with a yellow period appended." },
        { path: "hero.subtitle", type: "textarea", rows: 5, label: "Supporting paragraph",
          hint: "Right-column paragraph — keep to ~3 lines." },
        { path: "hero.cta_label", type: "text", label: "Primary CTA label" },
      ],
    },
    {
      id: "routing",
      label: "Audience routing",
      description:
        "Four editorial cards that route visitors to the right entry point (new project, careers, partnerships, press, etc.).",
      fields: [
        { path: "routing.eyebrow", type: "text", label: "Eyebrow" },
        { path: "routing.headline_main", type: "text", label: "Headline — main" },
        { path: "routing.headline_italic", type: "text", label: "Headline — italic word" },
        { path: "routing.headline_accent", type: "text", label: "Headline — accent word",
          hint: "Rendered in brand yellow with an underline. A period is appended automatically." },
        { path: "routing.description", type: "textarea", rows: 3, label: "Description" },
        { path: "routing.items", type: "service_list", variant: "panel",
          label: "Routes",
          itemNoun: "route", itemNounPlural: "routes",
          showIcon: true,
          labels: { title: "Card title", body: "Card body" },
          bodyRows: 3,
          extraFields: [
            { key: "tag_label", label: "Tag label (above title)", type: "text",
              placeholder: "New project" },
            { key: "cta_label", label: "CTA label", type: "text",
              placeholder: "Send a brief" },
            { key: "href", label: "CTA link", type: "text",
              placeholder: "mailto:hello@… or /path",
              hint: "Use `#contact` (or leave empty) to open the in-site Contact panel. Otherwise: mailto:, tel:, https:// or a relative /path." },
            { key: "is_primary", label: "Primary card (yellow)", type: "toggle",
              hint: "One card is typically primary — it opens the Contact panel when the link is empty." },
          ],
          defaultItem: { title: "New route", body: "" },
          hint: "Use ▲ / ▼ to reorder. 3–4 cards look best in the grid layout." },
      ],
    },
    {
      id: "book_a_call",
      label: "Book a call",
      description:
        "Cal.com-style direct booking block. Hidden today — turn \"Show section\" on when ready to publish.",
      fields: [
        { path: "book_a_call.visible", type: "toggle", label: "Show section",
          hint: "Off by default — the booking embed is hidden on the live site." },
        { path: "book_a_call.eyebrow", type: "text", label: "Eyebrow" },
        { path: "book_a_call.headline_main", type: "text", label: "Headline — main" },
        { path: "book_a_call.headline_italic", type: "text", label: "Headline — italic word" },
        { path: "book_a_call.headline_accent", type: "text", label: "Headline — accent word",
          hint: "Rendered in brand yellow with an underline. A period is appended automatically." },
        { path: "book_a_call.body", type: "textarea", rows: 4, label: "Supporting paragraph" },
        { path: "book_a_call.bullets", type: "service_list", variant: "panel",
          label: "Bullets",
          itemNoun: "bullet", itemNounPlural: "bullets",
          showBody: false,
          labels: { title: "Bullet text" },
          defaultItem: { title: "New bullet" },
          hint: "Short one-line bullets that appear under the paragraph." },
        { path: "book_a_call.embed_url", type: "url", label: "Cal.com embed URL",
          hint: "Full https:// link to your Cal.com booking page." },
        { path: "book_a_call.fallback_caption", type: "text", label: "Fallback caption",
          hint: "Shown under the embed — e.g. \"Trouble with the embed?\"" },
        { path: "book_a_call.fallback_cta_label", type: "text", label: "Fallback CTA label",
          hint: "Opens the Contact panel on click." },
      ],
    },
    {
      id: "faq",
      label: "FAQ",
      description:
        "Accordion of pre-call questions. Sticky sidebar on the left, accordion on the right.",
      fields: [
        { path: "faq.eyebrow", type: "text", label: "Sidebar eyebrow" },
        { path: "faq.headline_main", type: "text", label: "Sidebar headline — main" },
        { path: "faq.headline_italic", type: "text", label: "Sidebar headline — italic word" },
        { path: "faq.headline_accent", type: "text", label: "Sidebar headline — accent word",
          hint: "Rendered in brand yellow with an underline. A period is appended automatically." },
        { path: "faq.description", type: "textarea", rows: 3, label: "Sidebar description" },
        { path: "faq.items", type: "service_list", variant: "panel",
          label: "Q & A",
          itemNoun: "question", itemNounPlural: "questions",
          labels: { title: "Question", body: "Answer" },
          bodyRows: 4,
          defaultItem: { title: "New question", body: "" },
          hint: "Use ▲ / ▼ to reorder. Numbered automatically by position." },
      ],
    },
    {
      id: "direct_details",
      label: "Direct details",
      description:
        "Email, social handles, and office cards. Left column has the headline + email + socials. Right column has the offices grid.",
      fields: [
        { path: "direct_details.visible", type: "toggle", label: "Show section",
          hint: "On = section appears on /contact. Off = entire 'Direct line' block is hidden (data is preserved)." },
        { path: "direct_details.eyebrow", type: "text", label: "Eyebrow" },
        { path: "direct_details.headline_main", type: "text", label: "Headline — main" },
        { path: "direct_details.headline_italic", type: "text", label: "Headline — italic word" },
        { path: "direct_details.headline_accent", type: "text", label: "Headline — accent word",
          hint: "Rendered in brand yellow with an underline. A period is appended automatically." },
        { path: "direct_details.email", type: "text", label: "Email address" },
        { path: "direct_details.socials", type: "service_list", variant: "panel",
          label: "Social links",
          itemNoun: "social", itemNounPlural: "socials",
          labels: { title: "Label", body: "URL" },
          bodyRows: 1,
          defaultItem: { title: "Instagram", body: "https://instagram.com/" },
          hint: "Label appears next to the arrow; URL is where it links." },
        { path: "direct_details.offices", type: "service_list", variant: "panel",
          label: "Office cards",
          itemNoun: "card", itemNounPlural: "cards",
          labels: { title: "Card title (yellow eyebrow)", body: "Address line 1" },
          bodyRows: 1,
          extraFields: [
            { key: "addr2", label: "Address line 2 / sub-title", type: "text" },
            { key: "note", label: "Footer note", type: "text",
              placeholder: "By appointment" },
          ],
          defaultItem: { title: "Office", body: "" },
          hint: "Use ▲ / ▼ to reorder. 3 cards fit best in the grid." },
      ],
    },
    {
      id: "closing",
      label: "Closing",
      description:
        "Contact-page Closing CTA (independent from the home-page Closing CTA shared across other pages).",
      fields: [
        { path: "closing.eyebrow", type: "text", label: "Eyebrow" },
        { path: "closing.headline_main", type: "text", label: "Headline — main" },
        { path: "closing.headline_italic", type: "text", label: "Headline — italic phrase" },
        { path: "closing.headline_accent", type: "text", label: "Headline — accent phrase",
          hint: "Rendered in brand yellow with an underline. A period is appended automatically." },
        { path: "closing.cta_label", type: "text", label: "CTA label" },
      ],
    },
    {
      id: "form",
      label: "Contact form — copy",
      description:
        "Text shown inside the right-hand brief panel that opens from every \"Get Started\" CTA. Field options, validation, and submission logic are locked. Leave any field blank to keep the current copy.",
      fields: [
        { path: "form.eyebrow", type: "text", label: "Top eyebrow",
          hint: "Small yellow caption above the headline. Default: \"The Brief\"." },
        { path: "form.reply_note", type: "text", label: "Right-side note",
          hint: "Small text on the right (e.g. \"Reply within 24h\")." },
        { path: "form.headline_main", type: "text", label: "Headline — main",
          hint: "First white words. Default: \"Tell us about\"." },
        { path: "form.headline_italic", type: "text", label: "Headline — italic word",
          hint: "Italic grey word(s) between main and accent. Default: \"your\"." },
        { path: "form.headline_accent", type: "text", label: "Headline — accent word",
          hint: "Brand-yellow underlined word. A period is appended automatically. Default: \"project\"." },
        { path: "form.lead", type: "textarea", label: "Lead paragraph", rows: 3,
          hint: "Short paragraph under the headline." },
        { path: "form.label_service", type: "text", label: "Service section eyebrow",
          hint: "Default: \"What you need\"." },
        { path: "form.help_service", type: "text", label: "Service section helper text",
          hint: "Small caption under the service eyebrow. Visitors can select multiple. Default: \"Select all that apply.\" Leave blank to hide." },
        { path: "form.label_scope", type: "text", label: "Scope section eyebrow",
          hint: "Default: \"Scope\"." },
        { path: "form.help_scope", type: "text", label: "Scope section helper text",
          hint: "Small caption under the scope eyebrow. Visitors can select multiple. Default: \"Choose one or more.\" Leave blank to hide." },
        { path: "form.label_about", type: "text", label: "About-you section eyebrow",
          hint: "Default: \"About you\"." },
        { path: "form.label_brief", type: "text", label: "Brief section eyebrow",
          hint: "Default: \"The brief\"." },
        { path: "form.ph_name", type: "text", label: "Name field placeholder" },
        { path: "form.ph_email", type: "text", label: "Email field placeholder" },
        { path: "form.ph_company", type: "text", label: "Company field placeholder" },
        { path: "form.ph_website", type: "text", label: "Website field placeholder" },
        { path: "form.ph_brief", type: "text", label: "Brief textarea placeholder" },
        { path: "form.submit_label", type: "text", label: "Submit button label",
          hint: "Default: \"Send the brief\"." },
        { path: "form.submit_loading", type: "text", label: "Submit button — sending state",
          hint: "Shown while the form is submitting. Default: \"Sending…\"." },
        { path: "form.required_legend", type: "text", label: "Required-fields legend",
          hint: "Tiny note next to the * symbol. Default: \"Required\"." },
        { path: "form.footer_a", type: "text", label: "Footer pill 1",
          hint: "Default: \"250+ stores\"." },
        { path: "form.footer_b", type: "text", label: "Footer pill 2",
          hint: "Default: \"10+ years\"." },
        { path: "form.footer_c", type: "text", label: "Footer pill 3",
          hint: "Default: \"Brooklyn-based\"." },
        { path: "form.contact_email_label", type: "text", label: "Contact email — label",
          hint: "Email shown bottom-right of the form (default: \"hello@sundaydigital.io\"). Used as the mailto target if no custom link is set." },
        { path: "form.contact_email_href", type: "text", label: "Contact email — link (optional)",
          hint: "Custom href override. Leave blank to use mailto: with the label above. Use e.g. \"mailto:hello@yourdomain.com\"." },
        { path: "form.contact_social_label", type: "text", label: "Social handle — label",
          hint: "Handle shown next to the email (default: \"@sundaydigital\")." },
        { path: "form.contact_social_href", type: "text", label: "Social handle — link",
          hint: "URL the handle links to (default: \"https://instagram.com/sundaydigital\"). Opens in a new tab." },
        { path: "form.req_service", type: "toggle", label: "Service — required",
          hint: "ON = visitor must pick at least one service before submitting." },
        { path: "form.req_scope", type: "toggle", label: "Scope — required",
          hint: "ON = visitor must pick at least one scope before submitting. Default OFF — no scope is pre-selected." },
        { path: "form.req_name", type: "toggle", label: "Name — required" },
        { path: "form.req_email", type: "toggle", label: "Email — required",
          hint: "Email format is always validated when something is typed, even if this is OFF." },
        { path: "form.req_company", type: "toggle", label: "Company — required" },
        { path: "form.req_website", type: "toggle", label: "Website — required" },
        { path: "form.req_brief", type: "toggle", label: "Brief — required" },
      ],
    },
  ],
};

// --------------------------- GLOBALS (Nav + Footer) ---------------------------
export const GLOBALS_SCHEMA = {
  key: "globals",
  label: "Nav & Footer",
  route: "/",
  skipSeo: true,
  skipRouteSlug: true,
  sections: [
    {
      id: "nav",
      label: "Nav",
      description:
        "Top bar + full-screen hamburger overlay. Logo, CTA, overlay page links, and the preview-only variants row.",
      fields: [
        { path: "nav.logo", type: "media", label: "Logo",
          hint: "Small PNG/SVG. Shown in the nav bar + the overlay header + the footer (reuses the same asset)." },
        { path: "nav.cta_label", type: "text", label: "CTA label",
          hint: "Yellow Get-Started button in the nav." },
        { path: "nav.cta_action", type: "select", label: "CTA action",
          options: [
            { value: "open_contact", label: "Open Contact panel" },
            { value: "external", label: "Link to URL" },
          ],
          hint: "Choose whether the CTA opens the in-site Contact panel or navigates to an external URL." },
        { path: "nav.cta_href", type: "text", label: "CTA URL",
          hint: "Only used when action = Link to URL. Supports `#contact` (open Contact panel), mailto:, tel:, https:// or /path." },
        { path: "nav.overlay_pages", type: "service_list", variant: "panel",
          label: "Overlay pages",
          itemNoun: "link", itemNounPlural: "links",
          labels: { title: "Label", body: "Sub-caption (small text on the right)" },
          bodyRows: 1,
          extraFields: [
            { key: "href", label: "URL / Path", type: "text",
              placeholder: "/shopify-experts-development",
              hint: "Internal `/path`, full URL, or `#contact` to open the Contact panel." },
            { key: "route", label: "Internal route (React Router)", type: "toggle",
              hint: "On = in-app navigation without a page reload. Ignored when href is `#contact` or external." },
          ],
          defaultItem: { title: "New link", body: "", href: "/", route: true },
          hint: "Use ▲ / ▼ to reorder. Numbered automatically by position." },
        { path: "nav.footer_email", type: "text", label: "Overlay footer email",
          hint: "Bottom-left of the hamburger overlay." },
        { path: "nav.footer_tagline", type: "text", label: "Overlay footer tagline",
          hint: "Bottom-left next to the email (e.g. \"New York · Remote\")." },
        { path: "nav.show_variants", type: "toggle", label: "Show \"Preview · Variants\" row",
          hint: "Shows dev-only variant links (V1–V6, Mkt · v2) in the hamburger overlay footer. Turn OFF to hide the entire row on every host, including preview." },
      ],
    },
    {
      id: "footer",
      label: "Footer",
      description:
        "Bottom-of-every-page footer. Logo, tagline, 3 reorderable link columns, copyright row and legal links.",
      fields: [
        { path: "footer.logo", type: "media", label: "Logo",
          hint: "Leave empty to reuse the Nav logo." },
        { path: "footer.tagline", type: "textarea", rows: 3, label: "Tagline paragraph",
          hint: "Short blurb under the logo." },
        { path: "footer.columns", type: "service_list", variant: "panel",
          label: "Link columns",
          itemNoun: "column", itemNounPlural: "columns",
          showBody: false,
          labels: { title: "Column heading" },
          extraFields: [
            { key: "links", label: "Links — one per line as `Label | /path`", type: "textarea",
              rows: 5,
              hint: "Each line: display label, then `|`, then URL. Use `#contact` to open the Contact panel. Example: `Development | /shopify-experts-development`." },
          ],
          defaultItem: { title: "New column", links: "" },
          hint: "Use ▲ / ▼ to reorder. 3 columns render best in the grid." },
        { path: "footer.copyright", type: "text", label: "Copyright line",
          hint: "Supports the token `{year}` which renders the current year." },
        { path: "footer.legal_links", type: "service_list", variant: "panel",
          label: "Legal links (right of copyright)",
          itemNoun: "link", itemNounPlural: "links",
          labels: { title: "Label", body: "URL — `/privacy`, `/terms`, `#contact`, or full URL" },
          bodyRows: 1,
          defaultItem: { title: "Privacy", body: "/privacy" },
          hint: "Use ▲ / ▼ to reorder. `/privacy` and `/terms` open the auto-generated legal pages." },
      ],
    },
  ],
};

// --------------------------- SHOWCASE (listing page) ---------------------------
export const SHOWCASE_SCHEMA = {
  key: "showcase",
  label: "Showcase (listing)",
  route: "/showcase",
  sections: [
    {
      id: "hero",
      label: "Hero",
      description:
        "Editorial hero — breadcrumb, eyebrow (auto-counted), \"Show / italic case / yellow .\" title, paragraph, and CTAs.",
      fields: [
        { path: "hero.breadcrumb", type: "text", label: "Breadcrumb label" },
        { path: "hero.eyebrow", type: "text", label: "Eyebrow",
          hint: "Use `{count}` for the number of case studies and `{stories}` for \"story / stories\" (auto-pluralised). Example: `Selected work · {count} {stories}`." },
        { path: "hero.title_lead", type: "text", label: "Title — first part" },
        { path: "hero.title_italic", type: "text", label: "Title — italic part",
          hint: "Rendered italic in muted gray. A yellow period is appended automatically." },
        { path: "hero.subtitle", type: "textarea", rows: 5, label: "Supporting paragraph" },
        { path: "hero.cta_primary_label", type: "text", label: "Primary CTA label" },
        { path: "hero.cta_secondary_label", type: "text", label: "Secondary link label" },
        { path: "hero.cta_secondary_href", type: "text", label: "Secondary link href",
          hint: "Use `#projects` to anchor to the project list, `#contact` to open the Contact panel, or any URL/path." },
      ],
    },
    {
      id: "marquee",
      label: "Brand marquee",
      description:
        "Running brand index at the bottom of the hero — defaults to the case-study names. Turn off auto-mode to curate manually.",
      fields: [
        { path: "marquee.use_auto", type: "toggle", label: "Auto-fill from published case studies",
          hint: "ON (default) = the marquee uses the names of all published case studies, in order. OFF = the manual list below is used." },
        { path: "marquee.items", type: "service_list", variant: "panel",
          label: "Manual marquee items",
          itemNoun: "item", itemNounPlural: "items",
          showBody: false,
          labels: { title: "Brand name (uppercase)" },
          defaultItem: { title: "Sunday Digital" },
          hint: "Only used when Auto-fill is OFF. Use ▲ / ▼ to reorder." },
      ],
    },
    {
      id: "closing",
      label: "Closing — \"The work continues\"",
      description:
        "Closing manifesto. Headline supports tokens that auto-count published case studies.",
      fields: [
        { path: "closing.eyebrow", type: "text", label: "Eyebrow" },
        { path: "closing.headline_lead", type: "text", label: "Headline — lead",
          hint: "Tokens: `{count}` (number, e.g. 11), `{count_word}` (lower-cased word: eleven), `{count_word_cap}` (capitalised: Eleven). Default: `{count_word_cap} stories.`" },
        { path: "closing.headline_italic", type: "text", label: "Headline — italic phrase" },
        { path: "closing.headline_accent", type: "text", label: "Headline — accent (yellow underline)",
          hint: "Tokens: `{next_word}` (twelve), `{next_word_cap}` (Twelve). Period is appended automatically." },
        { path: "closing.cta_primary_label", type: "text", label: "Primary CTA label" },
        { path: "closing.cta_secondary_label", type: "text", label: "Secondary link label" },
        { path: "closing.cta_secondary_href", type: "text", label: "Secondary link href",
          hint: "Use `#contact` to open the Contact panel, or any URL/path." },
      ],
    },
  ],
};

// --------------------------- LEGAL PAGES (Privacy, Terms) ---------------------------
// Both pages share an identical schema — only the slug, label and route differ.
const buildLegalSchema = (key, label, route) => ({
  key,
  label,
  route,
  sections: [
    {
      id: "header",
      label: "Header",
      description: "Page top — breadcrumb, eyebrow, title and \"last updated\" date.",
      fields: [
        { path: "header.breadcrumb", type: "text", label: "Breadcrumb label" },
        { path: "header.eyebrow", type: "text", label: "Eyebrow" },
        { path: "header.title", type: "text", label: "Page title" },
        { path: "header.last_updated", type: "text", label: "\"Last updated\" date",
          hint: "Free text. Example: `May 2, 2026`. Shown under the title." },
      ],
    },
    {
      id: "body",
      label: "Body",
      description:
        "Long-form legal copy. Markdown is supported — `# H1`, `## H2`, `### H3`, `**bold**`, `*italic*`, `- bullet`, `[label](url)`, blank line = new paragraph.",
      fields: [
        { path: "body.markdown", type: "textarea", rows: 24, label: "Body (Markdown)",
          hint: "Use blank lines between paragraphs. Headings: `# Title` / `## Subhead` / `### Sub-subhead`. Inline: `**bold**`, `*italic*`, `[link](https://…)`. Lists: lines starting with `- `." },
      ],
    },
  ],
});

export const PRIVACY_SCHEMA = buildLegalSchema("privacy", "Privacy Policy", "/privacy");
export const TERMS_SCHEMA   = buildLegalSchema("terms",   "Terms & Conditions", "/terms");

// --------------------------- CONVERSION OPTIMIZATION ---------------------------
export const CONVERSION_SCHEMA = {
  key: "conversion",
  label: "Conversion Optimization page",
  route: "/conversion-optimization",
  sections: [
    {
      id: "hero",
      label: "Hero",
      description:
        "Top of the /conversion-optimization page. Same layout as other service-page heroes (copy left, signature visual right).",
      fields: [
        { path: "hero.breadcrumb",          type: "text", label: "Breadcrumb",
          hint: "Yellow second crumb after \"Sunday\". Default: \"Conversion Optimization\"." },
        { path: "hero.eyebrow",             type: "text", label: "Eyebrow" },
        { path: "hero.title_line_1",        type: "text", label: "Title — line 1",
          hint: "First line of the hero headline. Default: \"Lift,\"." },
        { path: "hero.title_line_2",        type: "text", label: "Title — line 2",
          hint: "Start of the second line. Default: \"earned with\"." },
        { path: "hero.title_emphasis",      type: "text", label: "Title — italic word",
          hint: "Italic grey word in line 2. Default: \"process\"." },
        { path: "hero.title_accent",        type: "text", label: "Title — yellow accent phrase",
          hint: "Brand-yellow phrase at the end of line 2. Default: \"not guesses\"." },
        { path: "hero.subtitle",            type: "textarea", rows: 4, label: "Subtitle / lead" },
        { path: "hero.cta_primary_label",   type: "text", label: "Primary CTA label",
          hint: "Default: \"Start a project\"." },
        { path: "hero.cta_secondary_label", type: "text", label: "Secondary CTA label",
          hint: "Default: \"See our work\"." },
        { path: "hero.cta_secondary_href",  type: "text", label: "Secondary CTA link",
          hint: "Default: \"/showcase\"." },
      ],
    },
    {
      id: "why",
      label: "Why CRO matters",
      description:
        "Editorial integration paragraph — reads as a quiet positioning statement that CRO is part of our process, not a separate retainer.",
      fields: [
        { path: "why.eyebrow",     type: "text", label: "Eyebrow" },
        { path: "why.main_lead",   type: "text", label: "Headline — main",
          hint: "First white words. Default: \"Traffic without conversion is a tax,\"." },
        { path: "why.main_italic", type: "text", label: "Headline — italic",
          hint: "Italic grey closing words. Default: \"not an asset.\"." },
        { path: "why.body",        type: "textarea", rows: 5, label: "Body paragraph" },
      ],
    },
    {
      id: "approach",
      label: "Approach — five steps",
      description:
        "Numbered chapter list. Each item: title, tag, and body. Reorder via ▲/▼. Empty list falls back to the hardcoded defaults.",
      fields: [
        { path: "approach.eyebrow",         type: "text", label: "Eyebrow" },
        { path: "approach.headline_main",   type: "text", label: "Headline — main",
          hint: "Default: \"Five steps,\"." },
        { path: "approach.headline_italic", type: "text", label: "Headline — italic",
          hint: "Default: \"one cycle.\"." },
        { path: "approach.description", type: "textarea", rows: 3, label: "Section description",
          hint: "Short paragraph next to the headline on desktop. Default mentions the analyze→iterate cycle." },
        { path: "approach.items", type: "service_list", variant: "panel",
          label: "Steps",
          itemNoun: "step", itemNounPlural: "steps",
          labels: { title: "Step title", body: "Body" },
          bodyRows: 4,
          extraFields: [
            { key: "tag", label: "Tag (uppercase caption under the title)", type: "text",
              placeholder: "Traffic · Behavior · Friction" },
          ],
          defaultItem: { title: "", body: "", tag: "" },
          hint: "Numbers are auto-generated from the order. Use ▲ / ▼ to reorder. Empty list = default 5 steps render on the public site." },
      ],
    },
    {
      id: "deliverables",
      label: "Core deliverables",
      description:
        "Six-tile grid of what's delivered. Same card style as the Marketing services grid.",
      fields: [
        { path: "deliverables.eyebrow",         type: "text", label: "Eyebrow" },
        { path: "deliverables.headline_main",   type: "text", label: "Headline — main",
          hint: "Default: \"What you get,\"." },
        { path: "deliverables.headline_italic", type: "text", label: "Headline — italic",
          hint: "Default: \"in writing.\"." },
        { path: "deliverables.items", type: "service_list", variant: "panel",
          label: "Deliverables",
          itemNoun: "deliverable", itemNounPlural: "deliverables",
          labels: { title: "Deliverable", body: "Description" },
          bodyRows: 4,
          defaultItem: { title: "", body: "" },
          hint: "Numbers are auto-generated. Use ▲ / ▼ to reorder. Empty list = default 6 deliverables render." },
      ],
    },
    {
      id: "connects",
      label: "How this connects",
      description:
        "Quiet integration block — reinforces that CRO is the lens we already use, not a standalone retainer. Keeps positioning intact.",
      fields: [
        { path: "connects.eyebrow",  type: "text", label: "Eyebrow" },
        { path: "connects.headline", type: "text", label: "Headline" },
        { path: "connects.body",     type: "textarea", rows: 5, label: "Body" },
      ],
    },
    { id: "testimonials", label: "Testimonials",
      description: "Auto-rotating testimonials block. Empty list = section auto-hidden on /conversion-optimization.",
      fields: [
        { path: "testimonials.eyebrow", type: "text", label: "Eyebrow" },
        { path: "testimonials.items", type: "service_list", variant: "panel",
          label: "Testimonials",
          itemNoun: "testimonial", itemNounPlural: "testimonials",
          labels: { title: "Quote", body: "Author name" },
          titleAsTextarea: true,
          extraFields: [
            { key: "role", label: "Role · Brand", type: "text", placeholder: "Owner · sclarandis.com",
              hint: "Format: \"Role · Brand\". Only the brand (right of \" · \") shows on the live site, in brand yellow." },
          ],
          defaultItem: { title: "", body: "", role: "" },
          hint: "Use ▲ / ▼ to reorder. Quotes rotate every 5 seconds. Leave empty to hide the block." },
      ],
    },
    { id: "closing_cta", label: "Closing CTA",
      description: "Shared final CTA band — edit it from /admin/content/home → Closing CTA." },
  ],
};

// --------------------------- CASE STUDY (virtual, site-wide) ---------------------------
// Not a route of its own — these settings apply to every /showcase/:slug
// page (e.g. site-wide testimonials shown under each case-study hero).
// `skipSeo: true` because individual case-study SEO is managed on the
// case-study document itself, not here.
export const CASE_STUDY_SCHEMA = {
  key: "case_study",
  label: "Showcase — Case-study pages",
  route: "/showcase",  // virtual — schema applies to every /showcase/:slug
  skipSeo: true,
  skipRouteSlug: true,
  sections: [
    { id: "testimonials", label: "Testimonials",
      description: "Site-wide testimonials block shown at the top of every /showcase/:slug page (right under the hero). Empty list = block auto-hidden across all case-study pages.",
      fields: [
        { path: "testimonials.eyebrow", type: "text", label: "Eyebrow" },
        { path: "testimonials.items", type: "service_list", variant: "panel",
          label: "Testimonials",
          itemNoun: "testimonial", itemNounPlural: "testimonials",
          labels: { title: "Quote", body: "Author name" },
          titleAsTextarea: true,
          extraFields: [
            { key: "role", label: "Role · Brand", type: "text", placeholder: "Owner · sclarandis.com",
              hint: "Format: \"Role · Brand\". Only the brand (right of the \" · \") is shown on the live site, in brand yellow." },
            { key: "slug", label: "Assign to showcase", type: "showcase_slug",
              hint: "Pick the case-study page where this review will appear. Each case study can have at most one review — the first match wins. Leave unassigned to park the testimonial for later." },
          ],
          defaultItem: { title: "", body: "", role: "", slug: "" },
          hint: "Use ▲ / ▼ to reorder. Each item must be assigned to a case-study page via the \"Assign to showcase\" dropdown — unassigned items show nowhere." },
      ],
    },
  ],
};

// --------------------------- HOMEPAGE REVIEW ---------------------------
// Private Shopify Homepage Review (/homepage-review). Has its own form
// (`POST /api/homepage-review` — same inbox as the Contact form, separate
// `homepage_review_submissions` collection). Every section's copy is
// editable; form options (Platform, Goals, Revenue ranges) are editable
// reorderable lists; each field's required state has its own toggle.
export const HOMEPAGE_REVIEW_SCHEMA = {
  key: "homepage_review",
  label: "Homepage Review page",
  route: "/homepage-review",
  sections: [
    {
      id: "hero",
      label: "Hero",
      description:
        "Top of the /homepage-review page. Confident, selective tone. CTA scrolls to the form.",
      fields: [
        { path: "hero.breadcrumb",       type: "text", label: "Breadcrumb (yellow crumb)",
          hint: "Default: \"Private Homepage Review\"." },
        { path: "hero.eyebrow",          type: "text", label: "Eyebrow",
          hint: "Small caps line above the title. Default: \"By invitation · selective\"." },
        { path: "hero.title_line_1",     type: "text", label: "Title — line 1",
          hint: "Default: \"Private Shopify\"." },
        { path: "hero.title_line_2",     type: "text", label: "Title — line 2 prefix",
          hint: "Words just before the yellow accent. Default: \"homepage\"." },
        { path: "hero.title_accent",     type: "text", label: "Title — yellow accent word",
          hint: "Brand-yellow underlined word, a period is auto-appended. Default: \"review\"." },
        { path: "hero.subtitle",         type: "textarea", rows: 3, label: "Subheadline" },
        { path: "hero.body",             type: "textarea", rows: 5, label: "Supporting copy" },
        { path: "hero.cta_label",        type: "text", label: "Primary CTA label",
          hint: "Default: \"Request a Homepage Review\"." },
        { path: "hero.trust_items", type: "service_list", variant: "panel",
          label: "Trust line items",
          itemNoun: "item", itemNounPlural: "items",
          showBody: false,
          labels: { title: "Label" },
          defaultItem: { title: "" },
          hint: "Small caps strip under the CTA. Defaults: \"For existing Shopify stores only\" · \"Reviewed manually\" · \"No initial commitment\". Leave list empty to hide the strip." },
      ],
    },
    {
      id: "why",
      label: "Why this exists",
      description:
        "Editorial integration paragraph next to a check-bullet list of key points.",
      fields: [
        { path: "why.eyebrow",         type: "text", label: "Eyebrow",
          hint: "Default: \"Why this exists\"." },
        { path: "why.headline_main",   type: "text", label: "Headline — main",
          hint: "Default: \"A clearer way to explore\"." },
        { path: "why.headline_italic", type: "text", label: "Headline — italic close",
          hint: "Italic grey words at the end. Default: \"what's possible.\"." },
        { path: "why.body_1", type: "textarea", rows: 5, label: "Paragraph 1" },
        { path: "why.body_2", type: "textarea", rows: 5, label: "Paragraph 2" },
        { path: "why.points", type: "service_list", variant: "panel",
          label: "Key points",
          itemNoun: "point", itemNounPlural: "points",
          showBody: false,
          labels: { title: "Point" },
          defaultItem: { title: "" },
          hint: "Yellow check-bullet list on the right side. Use ▲ / ▼ to reorder. Empty list = default 4 points render." },
      ],
    },
    {
      id: "receive",
      label: "What you may receive",
      description:
        "4-card grid describing what the homepage preview can include.",
      fields: [
        { path: "receive.eyebrow",         type: "text", label: "Eyebrow",
          hint: "Default: \"What you may receive\"." },
        { path: "receive.headline_main",   type: "text", label: "Headline — main",
          hint: "Default: \"What the homepage preview\"." },
        { path: "receive.headline_italic", type: "text", label: "Headline — italic close",
          hint: "Default: \"can include.\"." },
        { path: "receive.intro", type: "textarea", rows: 4, label: "Intro paragraph" },
        { path: "receive.cards", type: "service_list", variant: "panel",
          label: "Cards",
          itemNoun: "card", itemNounPlural: "cards",
          labels: { title: "Card title", body: "Card body" },
          bodyRows: 4,
          defaultItem: { title: "", body: "" },
          hint: "Numbers (01–04…) are auto-generated. Use ▲ / ▼ to reorder. Empty list falls back to the default 4 cards." },
      ],
    },
    {
      id: "not",
      label: "What this is not",
      description:
        "Sets clear expectations. Two-column bullet list of what the review is NOT, plus a closing italic line.",
      fields: [
        { path: "not.eyebrow",         type: "text", label: "Eyebrow",
          hint: "Default: \"Clear expectations from the start\"." },
        { path: "not.headline_main",   type: "text", label: "Headline — main",
          hint: "Default: \"What this is\"." },
        { path: "not.headline_italic", type: "text", label: "Headline — italic close",
          hint: "Default: \"not.\"." },
        { path: "not.body", type: "textarea", rows: 4, label: "Lead paragraph" },
        { path: "not.bullets", type: "service_list", variant: "panel",
          label: "Bullets",
          itemNoun: "bullet", itemNounPlural: "bullets",
          showBody: false,
          labels: { title: "Bullet text" },
          defaultItem: { title: "" },
          hint: "Each bullet renders with a small \"×\" marker. Empty list = default 4 bullets." },
        { path: "not.closing", type: "textarea", rows: 4, label: "Closing italic paragraph",
          hint: "Italic, slightly larger, sits below the bullets." },
      ],
    },
    {
      id: "how",
      label: "How it works",
      description:
        "Three numbered steps describing the review process. Numbers auto-generate.",
      fields: [
        { path: "how.eyebrow",         type: "text", label: "Eyebrow",
          hint: "Default: \"How the review works\"." },
        { path: "how.headline_main",   type: "text", label: "Headline — main",
          hint: "Default: \"Three steps,\"." },
        { path: "how.headline_italic", type: "text", label: "Headline — italic close",
          hint: "Default: \"no fluff.\"." },
        { path: "how.steps", type: "service_list", variant: "panel",
          label: "Steps",
          itemNoun: "step", itemNounPlural: "steps",
          labels: { title: "Step title", body: "Step body" },
          bodyRows: 4,
          defaultItem: { title: "", body: "" },
          hint: "Numbers (01, 02, 03…) are auto-generated. Use ▲ / ▼ to reorder. Empty list = default 3 steps." },
      ],
    },
    {
      id: "who",
      label: "Who this is for",
      description:
        "Two side-by-side panels — \"Good fit\" (yellow) and \"Not ideal for\" (muted). Each has its own editable bullet list and sub-heading.",
      fields: [
        { path: "who.eyebrow",         type: "text", label: "Eyebrow",
          hint: "Default: \"Built for existing Shopify brands\"." },
        { path: "who.headline_main",   type: "text", label: "Headline — main",
          hint: "Default: \"Who this is\"." },
        { path: "who.headline_italic", type: "text", label: "Headline — italic close",
          hint: "Default: \"for.\"." },
        { path: "who.body", type: "textarea", rows: 4, label: "Lead paragraph" },
        // Good fit
        { path: "who.good_label",      type: "text", label: "Good fit — small caps label",
          hint: "Default: \"Good fit\"." },
        { path: "who.good_subheading", type: "text", label: "Good fit — sub-heading",
          hint: "Default: \"Ideal for these brands\"." },
        { path: "who.good_items", type: "service_list", variant: "panel",
          label: "Good fit — bullets",
          itemNoun: "bullet", itemNounPlural: "bullets",
          showBody: false,
          labels: { title: "Bullet text" },
          defaultItem: { title: "" },
          hint: "Right-side count badge displays the total automatically. Empty list = default 4." },
        // Not ideal
        { path: "who.not_label",      type: "text", label: "Not ideal — small caps label",
          hint: "Default: \"Not ideal for\"." },
        { path: "who.not_subheading", type: "text", label: "Not ideal — sub-heading",
          hint: "Default: \"Better paths elsewhere\"." },
        { path: "who.not_items", type: "service_list", variant: "panel",
          label: "Not ideal — bullets",
          itemNoun: "bullet", itemNounPlural: "bullets",
          showBody: false,
          labels: { title: "Bullet text" },
          defaultItem: { title: "" },
          hint: "Empty list = default 4." },
      ],
    },
    {
      id: "form",
      label: "Form — copy, options & required toggles",
      description:
        "All form section copy, the editable option lists (Platform, Goals, Revenue ranges), each field's required toggle, and the success message.",
      fields: [
        { path: "form.eyebrow",         type: "text", label: "Section eyebrow",
          hint: "Default: \"Request your private homepage review\"." },
        { path: "form.headline_main",   type: "text", label: "Headline — main",
          hint: "Default: \"Tell us about\"." },
        { path: "form.headline_italic", type: "text", label: "Headline — italic close",
          hint: "Default: \"your store.\"." },
        { path: "form.intro", type: "textarea", rows: 4, label: "Form intro paragraph" },
        // Labels & placeholders
        { path: "form.label_name",     type: "text", label: "Name — field label",
          hint: "Default: \"Name\"." },
        { path: "form.ph_name",        type: "text", label: "Name — placeholder",
          hint: "Default: \"Your name\"." },
        { path: "form.label_email",    type: "text", label: "Email — field label",
          hint: "Default: \"Email\"." },
        { path: "form.ph_email",       type: "text", label: "Email — placeholder",
          hint: "Default: \"you@company.com\"." },
        { path: "form.label_website",  type: "text", label: "Website — field label",
          hint: "Default: \"Website URL\"." },
        { path: "form.ph_website",     type: "text", label: "Website — placeholder",
          hint: "Default: \"https://yourstore.com\"." },
        { path: "form.label_platform", type: "text", label: "Platform — field label",
          hint: "Default: \"Current platform\"." },
        { path: "form.label_goals",    type: "text", label: "Goals — field label",
          hint: "Default: \"What are you looking to improve?\"." },
        { path: "form.help_goals",     type: "text", label: "Goals — helper caption",
          hint: "Small caption under the label. Default: \"Select all that apply.\". Leave blank to hide." },
        { path: "form.label_revenue",  type: "text", label: "Revenue — field label",
          hint: "Default: \"Estimated monthly revenue\"." },
        { path: "form.revenue_optional_label", type: "text", label: "Revenue — optional tag",
          hint: "Small grey \"(optional)\" tag next to the label. Default: \"(optional)\". Blank to hide." },
        { path: "form.label_message",  type: "text", label: "Message — field label",
          hint: "Default: \"Short message / notes\"." },
        { path: "form.message_optional_label", type: "text", label: "Message — optional tag",
          hint: "Default: \"(optional)\". Blank to hide." },
        { path: "form.ph_message",     type: "text", label: "Message — placeholder",
          hint: "Default mentions \"Anything you'd like us to know…\"." },
        // Submit + small text
        { path: "form.submit_label",    type: "text", label: "Submit button label",
          hint: "Default: \"Submit Review Request\"." },
        { path: "form.submit_loading",  type: "text", label: "Submit button — sending state",
          hint: "Default: \"Sending…\"." },
        { path: "form.small_text",      type: "textarea", rows: 3, label: "Small text under submit button",
          hint: "Quiet line next to the CTA. Default: \"Every request is reviewed manually. A homepage preview is prepared only when we see a clear opportunity and strong fit.\"." },
        // Required toggles
        { path: "form.req_message", type: "toggle", label: "Message — required",
          hint: "ON = visitor must fill the notes textarea. Default OFF." },
        { path: "form.req_platform", type: "toggle", label: "Platform — required",
          hint: "ON = visitor must pick a platform. Default OFF (pre-selected to Shopify)." },
        { path: "form.req_goals", type: "toggle", label: "Goals — at least one required",
          hint: "ON = visitor must pick at least one goal. Default OFF." },
        { path: "form.req_revenue", type: "toggle", label: "Revenue — required",
          hint: "ON = visitor must pick a revenue range. Default OFF." },
        // Option lists
        { path: "form.platform_options", type: "service_list", variant: "panel",
          label: "Platform options",
          itemNoun: "option", itemNounPlural: "options",
          showBody: false,
          labels: { title: "Label" },
          defaultItem: { title: "" },
          hint: "First option in the list is selected by default. Reorder with ▲ / ▼. Empty list = default Shopify / Shopify Plus / Other." },
        { path: "form.goal_options", type: "service_list", variant: "panel",
          label: "\"What are you looking to improve?\" options",
          itemNoun: "option", itemNounPlural: "options",
          showBody: false,
          labels: { title: "Label" },
          defaultItem: { title: "" },
          hint: "Multi-select pills. Reorder with ▲ / ▼. Empty list = default 7 options." },
        { path: "form.revenue_options", type: "service_list", variant: "panel",
          label: "Revenue range options",
          itemNoun: "range", itemNounPlural: "ranges",
          showBody: false,
          labels: { title: "Range label" },
          defaultItem: { title: "" },
          hint: "Single-select toggle pills. Reorder with ▲ / ▼. Empty list = default 5 ranges (Under $50k → $5M+)." },
        // Success view
        { path: "form.success_eyebrow", type: "text", label: "Success — eyebrow",
          hint: "Default: \"Request received\"." },
        { path: "form.success_headline", type: "text", label: "Success — headline",
          hint: "Default: \"Thank you — your request has been received.\"." },
        { path: "form.success_body", type: "textarea", rows: 4, label: "Success — paragraph",
          hint: "Default explains the manual review + private preview path." },
      ],
    },
    {
      id: "final",
      label: "Final CTA",
      description:
        "Last section before the footer. Centered headline + CTA + trust strip.",
      fields: [
        { path: "final.eyebrow",         type: "text", label: "Eyebrow",
          hint: "Default: \"Take the first look\"." },
        { path: "final.headline_main",   type: "text", label: "Headline — main",
          hint: "Default: \"Want to see what your Shopify homepage\"." },
        { path: "final.headline_italic", type: "text", label: "Headline — italic close",
          hint: "Default: \"could become?\"." },
        { path: "final.body", type: "textarea", rows: 4, label: "Body paragraph" },
        { path: "final.cta_label", type: "text", label: "CTA label",
          hint: "Default: \"Request a Homepage Review\". CTA always scrolls to the form." },
        { path: "final.trust_items", type: "service_list", variant: "panel",
          label: "Trust line items",
          itemNoun: "item", itemNounPlural: "items",
          showBody: false,
          labels: { title: "Label" },
          defaultItem: { title: "" },
          hint: "Same default values as the hero trust strip. Empty list = hide." },
      ],
    },
  ],
};


export const ALL_SCHEMAS = [
  HOME_SCHEMA,
  SHOPIFY_DEV_SCHEMA,
  MARKETING_SCHEMA,
  CREATIVE_SCHEMA,
  ABOUT_SCHEMA,
  CONTACT_SCHEMA,
  SHOWCASE_SCHEMA,
  CASE_STUDY_SCHEMA,
  CONVERSION_SCHEMA,
  HOMEPAGE_REVIEW_SCHEMA,
  PRIVACY_SCHEMA,
  TERMS_SCHEMA,
  GLOBALS_SCHEMA,
];

/* ===== SEO section (appended to every page, identical fields) =====
 *
 * `kind: "seo"` tells the ContentEditPage to save these fields to
 * `/api/seo` (route = the page's public route) instead of into the
 * content document. SEO edits are live immediately (no draft state
 * on the backend yet). A hint is surfaced in the form.
 */
const SEO_SECTION = {
  id: "seo",
  kind: "seo",
  label: "SEO",
  description:
    "Search-engine & social-share metadata for this page. Takes effect on Publish (just like content).",
  fields: [
    { path: "__route_path", type: "slug", label: "Page URL",
      hint: "Public path for this page. Type words or slugs — spaces and capitals are auto-cleaned. Leave empty to restore the original." },
    { path: "title", type: "text", label: "Meta title",
      hint: "Shown on Google search results and browser tabs. ~50–60 chars." },
    { path: "description", type: "textarea", rows: 3, label: "Meta description",
      hint: "Shown under the title in search results. ~150–160 chars." },
    { path: "og_title", type: "text", label: "Open Graph title",
      hint: "Title when shared on Facebook/LinkedIn. Leave empty to reuse Meta title." },
    { path: "og_description", type: "textarea", rows: 3, label: "Open Graph description",
      hint: "Description when shared on social. Leave empty to reuse Meta description." },
    { path: "og_image", type: "media", label: "Social share image",
      hint: "1200×630 recommended. Shown when the page is shared on Facebook, LinkedIn, Slack, etc." },
    { path: "canonical", type: "url", label: "Canonical URL",
      hint: "Optional. Full URL including https://. Useful to consolidate duplicate pages." },
    { path: "noindex", type: "toggle", label: "Hide from search engines",
      hint: "Turn on to add `noindex, nofollow` — page won't appear in Google." },
  ],
};

// Append SEO to every page schema (reference-shared is fine — read-only config).
// Pages that set `skipSeo: true` (e.g. the Globals Nav/Footer meta-page) are
// skipped — they don't have a public URL of their own.
ALL_SCHEMAS.forEach((s) => {
  if (s.skipSeo) return;
  if (!s.sections.some((sec) => sec.id === "seo")) {
    s.sections.push(SEO_SECTION);
  }
});

export const SCHEMA_BY_KEY = Object.fromEntries(
  ALL_SCHEMAS.map((s) => [s.key, s])
);
